'use client';
import './sider.scss';
/* global __esri */
/* Map Related library */
import Graphic from '@arcgis/core/Graphic';
import * as tj from '@mapbox/togeojson';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polygon from '@arcgis/core/geometry/Polygon';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import TextSymbol from '@arcgis/core/symbols/TextSymbol.js';

/* design library */

import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import Tab from '@mui/material/Tab';

/* NextJS library */
import { useRouter } from 'next/navigation';
/* I18n library  */
import { useTranslation } from 'react-i18next';
/* eslint-disable @next/next/no-img-element */
/* React Library */
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/* ThirdParty library */
import { useDebouncedCallback } from 'use-debounce';

import moment from 'moment';

/* Constants and Enums */
import { imageTypes } from '@/constants/enums/enums';

import orderType from '@/constants/enums/orderType';
import { fileExt } from '@/helper/constant';

/* import context & helpers */
import { useShoppingCart } from '@/context/ShoppingCartContext';
import { calculateExistingItemPrice } from '@/helper/cartHelper';

/* Server Actions */
import { getGuestToken } from '@/network/serverApis/guestToken';

/* Components */

import { MapContext } from '../../context/MapContext/MapProvider';
import customToaster from '../toast/CustomToaster';
import UploadDialog from './Dialog/uploadDialog';
import { getLocationNameFromLongitudeAndLatitude } from '@/network/serverApis/EsriArcGIS';

import {
  calculateArea,
  checkPanchromatic,
  convertDMSCoordinateToDD,
  existingDataCaptureDateRange,
  formatAreaAndCloudCoverageForFilter,
  formatAreaUptoDecimalPoint,
  formatBundleName,
  formatResolutionValueForFilter,
  getDeviceType,
  getOfferingIdSourceAndDataType,
  mixPanelDateFormat,
  priceNumberFormat,
  roundOffUptoTwo,
  TODO,
} from '@/lib/utils';

import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEvent, MixpanelEventHub } from '@/services/eventEnums';

import { useForm } from 'react-hook-form';

import { useSpinnerContext } from '@/context/SpinnerContext';
import { triggerEvent } from '@/lib/events';
import { useAgentSiderEvents } from '@/features/hub-agent/hooks/useAgentSiderEvents';

import {
  getBlackSkyPointCircleGraphic,
  getCircleGraphicsAndSquarePolygonFromCenter,
  getRectangleFromCenter,
  translatePolygon,
} from '../map/utils/utils';
import SearchBar from './searchBar';
import ExploreExisting from './exploreExisting';
import { AddMultiProductDialog } from './Dialog/AddMultiProductDialog';
import { GeoResult, ProductCardType, WebSocketData } from './sider.types';
import { landingScreenArchiveDTOptions } from '@/constants';
import posthog from 'posthog-js';

import K from '@/constants/constants';

import TaskingForm from './orderNew/taskingForm';
import customToasterUpdater from '../toast/CustomToastUpdater';
import { loadPolygonDataFromSearchParams } from '../../context/MapContext/mapUtils';
import { useAuthenticationContext } from '@/context/authenticationProvider';
import MobileIntoDialog from './mobileIntro/mobileIntroDialog';
import { getUiElements } from '@/network/serverApis/existing';

export default function Sider({
  isHub,
  authError,
  url,
  searchData,
}: {
  isHub: boolean;
  authError: string;
  url?: string;
  searchData: {
    source?: string | null;
    from?: string | null;
    to?: string | null;
    coordinates?: any | null;
    resolution?: string | null;
    valid: boolean;
    tab?: any | null;
    errorMessage: string;
  };
}) {
  const {
    cartQuantity,
    cartItems,
    isNew,
    setIsNew,
    mergeOverlapPrimaryItemRef,
    mergeOverlapRef,
    mergeItemRef,
    disableBandsRef,
    disableConstellationRef,
    disableSrcRef,
    tabValue,
    multiProduct,
    multiProductCartItemRef,
    setMultiProduct,
    removeAndHighlightLayerCbRef,
    openPreviewInfoPopup,
    setOpenPreviewInfoPopup,
    archiveUiElementsRef,
    setAoiHeightWidth,
    aoiHeightWidth,
    orderNewIsGuaranteedCapture,
    confirmMergeItemCart,
    increaseCartQuantity,
    removeFromCart,
    resetCart,
    openCart,
  } = useShoppingCart();
  const {
    quickSketchVM,
    sketchVM,
    view,
    previewLayer,
    intersectLayer,
    sarSelectedRef,
    zoom,
    measurementLayerRef,
    polylineLabelRef,
  } = useContext(MapContext);
  const { getAuthenticatedToken, userData } = useAuthenticationContext();
  const router = useRouter();
  //explore existing product card ref
  const productCardContainerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation('orderImagery');
  // const [value, setValue] = React.useState('1');
  const [tabButtonValue, setTabButtonValue] = React.useState('1');

  // Search bar related states
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState<
    {
      label: string;
      magicKey: string;
    }[]
  >([]);
  const [selectDataType, setSelectDataType] = useState<any>(['all']);
  const selectDataTypeRef = useRef(['all']);
  // WebSocket related states

  const webSocketRef = useRef<WebSocket | null>(null);

  const [polygonCoordinates, setPolygonCoordinates] = useState<number[][][]>([[[]]]);
  const [isFetchingData, SetIsFetchingData] = useState(false);
  const [products, SetProducts] = useState<ProductCardType[]>([]);
  const [progress, setProgress] = useState(0);
  const extentRef = useRef<any>(null);
  const spatialReferenceRef = useRef<any>(null);
  const staticRectangleRef = useRef<{ width: number; height: number } | null>(null);
  const originalPolygonGraphicRef = useRef<Graphic | null>(null);
  // Persists the single BlackSky tasking point across source switches so it can
  // be hidden (not destroyed) when leaving BlackSky and restored when returning.
  const currentPointRef = useRef<Graphic | null>(null);
  // The visual footprint circle drawn around currentPointRef — rebuilt (not
  // persisted as-is) whenever the point is (re)placed, since its radius
  // depends on the currently selected BlackSky source's scene size.
  const currentPointCircleRef = useRef<Graphic | null>(null);
  // Last known average_scene_size_sqkm for the selected BlackSky source
  // (Gen-2: 33, Gen-3: 25), used to size currentPointCircleRef.
  const blackskyAreaSqkmRef = useRef<number | null>(null);
  const [showSkeleton, setShowSkeleton] = useState(false);

  const [isArchiveLandingScreen, setIsArchiveLandingScreen] = useState(true);
  const isArchiveLandingScreenRef = useRef<boolean>(true);
  const [isPosthogFlagLoaded, setIsPosthogFlagLoaded] = useState(false);
  const [placeholder, setPlaceholder] = useState<any>(false);

  const isAllowMaxAoiSize = posthog.isFeatureEnabled('enable_allow_max_aoi_size');

  const [flowData, setFlowData] = useState();
  const viewWhenRef = useRef<any>(null);
  const { startDate: calculatedStartDate, endDate: calculatedEndDate } =
    existingDataCaptureDateRange();

  const [filterStartDate, setFilterStartDate] = useState(
    searchData.from
      ? moment(searchData.from).format('YYYY-MM-DD')
      : calculatedStartDate.toISOString().split('T')[0]
  );

  const [filterLastDate, setFilterLastDate] = useState(
    searchData.to
      ? moment(searchData.to).format('YYYY-MM-DD')
      : calculatedEndDate.toISOString().split('T')[0]
  );

  const buttonRef = useRef<HTMLDivElement>(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      startDate: filterStartDate,
      lastDate: filterLastDate,
    },
  });
  const { startLoaderEvent, stopLoaderEvent } = useSpinnerContext();

  useEffect(() => {
    // if there is auth sign in or sign up error redirect user to the homepage
    if (authError) {
      customToaster(`${authError} ${t('unauthorized:invalidSignIn')}.`);

      setTimeout(() => {
        // router.push(`/auth/logout?returnTo=${url}`);
        router.push(`/auth/logout`);
      }, 3000);
    }
  }, [stopLoaderEvent, startLoaderEvent, authError, router, url, t]);

  const gettingUiElements = async () => {
    let endPoint = 'archive-search';
    let token = await getAuthenticatedToken();
    if (!token) {
      // eslint-disable-next-line require-use-server
      token = await getGuestToken();
    }
    let response: any;

    // For using the local archive json uncomment the following code and update teh "archive.json" file
    //   // eslint-disable-next-line require-use-server
    // response = await getOrderNewFlowsData('archive.json');

    // eslint-disable-next-line require-use-server
    response = await getUiElements(endPoint, token);
    if (response?.id) {
      archiveUiElementsRef.current = response;
      setFlowData(response);
      triggerEvent(stopLoaderEvent);
    } else {
      customToaster(t('common:generalError'));
      triggerEvent(stopLoaderEvent);
    }
  };
  const getUiDataConditionBased = async () => {
    triggerEvent(startLoaderEvent);

    const deviceTypeValue = getDeviceType();
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY || '', {
      api_host:
        process.env.NEXT_PUBLIC_ENABLE_REWRITE === 'true'
          ? process.env.NEXT_PUBLIC_POSTHOG_PROXY_URL
          : process.env.NEXT_PUBLIC_POSTHOG_HOST,
      ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,

      capture_pageview: true,
      autocapture: false,
      //this is in debugging mode remove this to use in production
      debug: process.env.NEXT_PUBLIC_POSTHOG_DEBUG_VALUE === 'true',
      loaded: (posthog: any) => {
        posthog.setPersonPropertiesForFlags({
          origin: process.env.NEXT_PUBLIC_PLATFORM_APP,
          $device_type: deviceTypeValue,
        });
      },
    });

    const featureFlagCallback = () => {
      setIsPosthogFlagLoaded(true);

      const sixMonthBeforeDate = moment().subtract(6, 'M').format('YYYY-MM-DD');
      if (!searchData?.from && !searchData?.to) {
        setFilterStartDate(sixMonthBeforeDate);
        setValue('startDate', sixMonthBeforeDate);
      }

      if (tabValue.current === K.TABS_VALUES.ARCHIVE && !archiveUiElementsRef.current) {
        gettingUiElements();
      } else {
        triggerEvent(stopLoaderEvent);
      }
    };
    // this is need to be refactored
    featureFlagCallback();
  };

  const calculateAoiHeightAndWidth: any = (event: any) => {
    if (staticRectangleRef.current) {
      if (sketchVM?.layer?.graphics?.length === 0) return { width: null, height: null };
      return { width: staticRectangleRef.current.width, height: staticRectangleRef.current.height };
    }
    const graphic = event?.graphic ? event?.graphic : event?.graphics?.[0];
    const extent = graphic?.geometry?.extent;
    const xmin = extent?.xmin;
    const ymin = extent?.ymin;
    const xmax = extent?.xmax;
    const ymax = extent?.ymax;
    const spatialReference = { wkid: 102100 };
    const widthLine = new Polyline({
      paths: [
        [
          [xmin, ymin],
          [xmax, ymin],
        ],
      ],
      spatialReference,
    });

    const heightLine = new Polyline({
      paths: [
        [
          [xmin, ymin],
          [xmin, ymax],
        ],
      ],
      spatialReference,
    });
    const width = geometryEngine.geodesicLength(widthLine, 'kilometers');
    const height = geometryEngine.geodesicLength(heightLine, 'kilometers');
    return { width, height };
  };

  useLayoutEffect(() => {
    if (!stopLoaderEvent) return;
    triggerEvent(stopLoaderEvent);

    getUiDataConditionBased();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopLoaderEvent, startLoaderEvent]);

  useEffect(() => {
    if (!buttonRef) return;

    if (!sketchVM) return;

    const polylineMeasurementToolEvent = (graphics: any) => {
      measurementLayerRef.current = graphics;

      graphics.symbol = {
        type: 'simple-line',
        color: [0, 0, 0, 1],
        width: 2,
        style: 'short-dash',
      };

      const distance = geometryEngine.geodesicLength(graphics.geometry, 'kilometers');

      const midpoint = graphics?.geometry?.extent?.center;

      const textSymbol = new TextSymbol({
        text: `${formatAreaUptoDecimalPoint(distance, 3)} km`,
        color: 'white',
        haloColor: 'black',
        haloSize: 1,
        font: {
          size: 12,
          weight: 'bold',
        },
      });

      if (distance > 0) {
        if (!polylineLabelRef.current) {
          const labelGraphic = new Graphic({
            geometry: midpoint,
            symbol: textSymbol,
          });
          sketchVM?.layer?.add(labelGraphic);
          polylineLabelRef.current = labelGraphic;
        } else {
          polylineLabelRef.current.symbol = textSymbol;
          polylineLabelRef.current.geometry = midpoint;
        }
      }
      return;
    };

    const restoreOriginalPolygon = () => {
      const origGraphic = originalPolygonGraphicRef.current as Graphic & { geometry: Polygon };
      originalPolygonGraphicRef.current = null;
      sketchVM.cancel();
      sketchVM.layer.removeAll();
      origGraphic.visible = true;
      sketchVM.layer.add(origGraphic);
      sketchVM.update(origGraphic);
      let coordinates = origGraphic.geometry?.rings;
      if (origGraphic.geometry?.spatialReference?.isWebMercator) {
        coordinates = [
          origGraphic.geometry.rings[0].map((value: any) =>
            webMercatorUtils.xyToLngLat(value[0], value[1])
          ),
        ];
      }
      setPolygonCoordinates(coordinates);
    };

    const saveOriginalPolygon = (graphic: (Graphic & { geometry: Polygon }) | null) => {
      const isSpecialModeGraphic =
        graphic?.attributes?.blackskyPoint ||
        graphic?.attributes?.blackskyPointCircle ||
        graphic?.attributes?.circle ||
        graphic?.attributes?.staticRectangle ||
        graphic?.attributes?.staticRectangleCenter;
      if (graphic && !isSpecialModeGraphic) {
        originalPolygonGraphicRef.current = graphic;
      }
    };

    const customEventUpdate = sketchVM.on('update', async (event: TODO) => {
      const value = calculateAoiHeightAndWidth(event);

      setAoiHeightWidth({ width: value.width, height: value.height });
      if (
        (event?.state === 'active' && event?.toolEventInfo?.type === 'move-stop') ||
        event?.toolEventInfo?.type === 'reshape-stop'
      ) {
        // For Polyline Measurement tool
        if (event?.graphics?.[0]?.geometry?.type === 'polyline') {
          const polineGraphics = event?.graphics?.[0];
          polylineMeasurementToolEvent(polineGraphics);
        }

        const extent = event?.graphics?.[0]?.geometry?.get('extent');
        !view?.extent?.contains(extent) && zoom.zoomOut();
      }
    });

    const customEventUpdate2 = sketchVM.on('update', async (event: TODO) => {
      const value = calculateAoiHeightAndWidth(event);
      setAoiHeightWidth({ width: value.width, height: value.height });

      if (
        (event.state === 'active' && event.toolEventInfo?.type === 'move-stop') ||
        event.toolEventInfo?.type === 'reshape-stop'
      ) {
        if (event?.graphic?.geometry?.type === 'polyline') {
          const polineGraphics = event?.graphic;
          polylineMeasurementToolEvent(polineGraphics);
        }
      }
      if (event.state === 'start' || event.toolEventInfo?.type === 'reshape-stop') {
        if (event?.graphic?.geometry?.type === 'polyline') {
          const polineGraphics = event?.graphic;
          polylineMeasurementToolEvent(polineGraphics);
        }
        //change the side vertex point circle colors
        sketchVM._internalGraphicsLayer.graphics.map((graphic: any) => {
          //color of the point
          if (graphic?.geometry?.type === 'point') {
            graphic.symbol.color = process.env.NEXT_PUBLIC_THEME_PRIMARY_COLOR;
          }
        });
      }
      if (
        (event.state === 'active' && event.toolEventInfo?.type === 'move-stop') ||
        event.toolEventInfo?.type === 'reshape-stop'
      ) {
        if (event?.graphic?.geometry?.type === 'polyline') {
          sketchVM.snappingOptions.featureSources.removeAll();
          return;
        }

        // Dragging the BlackSky point (tool: 'move') fires this same
        // move-stop/reshape-stop event. A Point has no centroid/rings — all
        // of this block below assumes a polygon, so handle the point here
        // instead: rebuild the footprint circle and the bounding-square AOI
        // around its new position, same as when the point is first created.
        if (event?.graphics?.[0]?.geometry?.type === 'point') {
          const movedPoint = event.graphics[0];
          currentPointRef.current = movedPoint;
          if (currentPointCircleRef.current) {
            sketchVM.layer.remove(currentPointCircleRef.current);
          }
          const centerPoint = movedPoint.geometry as __esri.Point;
          const { circleGraphic, squarePolygon } = getBlackSkyPointCircleGraphic(
            { x: centerPoint.longitude, y: centerPoint.latitude },
            blackskyAreaSqkmRef.current ?? 25
          );
          currentPointCircleRef.current = circleGraphic;
          sketchVM.layer.add(circleGraphic);
          setPolygonCoordinates(squarePolygon.rings);
          return;
        }

        const centroid: any = event?.graphics?.[0]?.geometry?.get('centroid');

        const longitude = centroid['longitude'];
        const latitude = centroid['latitude'];

        const rings: [[[]]] = event?.graphics?.[0]?.geometry?.get('rings');

        const extent = event?.graphics?.[0]?.geometry?.get('extent');
        extentRef.current = extent;
        spatialReferenceRef.current = 102100;
        //if sar is selected also translate the polygon while moving the sar cirlce
        if (sarSelectedRef.current === true) {
          const oldGraphic = sketchVM?.layer?.graphics?.getItemAt(0) as Graphic & {
            geometry: Polygon;
          };

          //if the old graphic is circle then do nothing
          //if the old graphic exist
          if (oldGraphic && !oldGraphic.attributes?.circle) {
            //if the spatial reference is 4326 or WGS84 then convert it into wkid 102100 for map accuracy
            if (oldGraphic.geometry.spatialReference?.isWGS84) {
              //also converting the polygon rings
              const newCoordinates = oldGraphic?.geometry?.rings?.[0]?.map((value: any) => {
                return webMercatorUtils.lngLatToXY(value[0], value[1]);
              });

              //creating new polygon of spatial reference wkid of 102100
              const newPolygon = new Polygon({
                rings: [newCoordinates],
                spatialReference: { wkid: 102100 },
              });

              //replacing and assigining the new polygon
              oldGraphic.geometry = newPolygon;

              //update the spatial reference to avoid the next loop(if any bug happen)
            }
            const oldGraphicPoint = {
              x: oldGraphic.geometry.centroid.x,
              y: oldGraphic.geometry.centroid.y,
            };
            const updatedGraphicsPoint = {
              x: centroid.x,
              y: centroid.y,
            };

            //move the polygon to the update area
            translatePolygon(oldGraphicPoint, updatedGraphicsPoint, oldGraphic.geometry);

            //also update the center for the next update
            //otherwise the polygon will be updated with old center point
            oldGraphic.geometry.centroid.x = centroid.x;
            oldGraphic.geometry.centroid.y = centroid.y;
          }
        }

        //clear or remove the previous intersected graphics
        intersectLayer.removeAll();
        // remove the previous preview images
        previewLayer.source.elements.removeAll();

        //convert the coordinate system to wkid 4326
        const ringsData = rings[0].map((value: any) => {
          return webMercatorUtils.xyToLngLat(value[0], value[1]);
        });

        // eslint-disable-next-line require-use-server
        const geoResult = await getLocationNameFromLongitudeAndLatitude(longitude, latitude);

        //setting state
        setSearchValue(geoResult?.address?.LongLabel || '');
        setPolygonCoordinates([ringsData]);

        if (!checkArea([ringsData])) return;

        //if the Order New tab is active the search call will be avoided
        if (buttonRef.current?.getAttribute('aria-selected') === 'true') {
          //clearing the intersection area(i.e. Order Area) value
          await sketchVM.emit('update', { state: 'clearIntersectArea' });
          if (
            // isArchiveNewDesignEnabledRef.current === false ||
            // isArchiveNewDesignEnabledRef.current === true &&
            isArchiveLandingScreenRef.current === false
          ) {
            getWebSocketPayload([ringsData]);
          }
        }

        //tracking

        if (event.toolEventInfo?.type === 'reshape-stop') {
          if (event?.graphics?.[0]?.geometry?.type === 'polyline') {
            sketchVM.snappingOptions.featureSources.removeAll();
            return;
          }
          let polygonArea = geometryEngine.geodesicArea(
            event.graphics[0].geometry,
            'square-kilometers'
          );
          // get offering id, source and data type from cart items
          const eventData = getOfferingIdSourceAndDataType(cartItems);

          if (MixpanelTracking.getInstance().hasProperty('siderOrderNew') == true) {
            MixpanelTracking.getInstance().track(
              MixpanelEvent.ORDER_NEW_RESIZE_POLYGON_AOI_DEFINED,
              {
                location: geoResult?.address?.LongLabel,
                coordinates: ringsData,
                area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
                offering_ids: eventData?.uniqueOfferingIds,
                sources: eventData?.uniqueSourceList,
                data_type: eventData?.uniqueDataTypes,
              }
            );
          } else {
            MixpanelTracking.getInstance().track(
              MixpanelEvent.EXPLORE_EXISTING_RESIZE_POLYGON_SEARCH,
              {
                location: geoResult?.address?.LongLabel,
                coordinates: ringsData,
                area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
                offering_ids: eventData?.uniqueOfferingIds,
                sources: eventData?.uniqueSourceList,
                data_type: eventData?.uniqueDataTypes,
              }
            );
          }
        }
      }

      if (event.state === 'fileUpload') {
        MixpanelTracking.getInstance().setProperty('savedAreasFileUpload', 'error', '');

        const fileReader = await handleFileUpload(event?.file);

        //if file reader on load has ran or started
        if (fileReader.onload) {
          fileReader.onloadend = event => {
            const savedAreaObject =
              MixpanelTracking.getInstance().getProperty('savedAreasFileUpload');

            let error = savedAreaObject && savedAreaObject?.error;

            MixpanelTracking.getInstance().track(MixpanelEventHub.HUB_MY_SAVED_AREAS_UPLOAD_AOI, {
              success: error ? `No, ${error}` : 'yes',
            });
          };
        }
        //if not ran because of extension error
        else {
          const savedAreaObject =
            MixpanelTracking.getInstance().getProperty('savedAreasFileUpload');

          let error = savedAreaObject && savedAreaObject?.error;

          MixpanelTracking.getInstance().track(MixpanelEventHub.HUB_MY_SAVED_AREAS_UPLOAD_AOI, {
            success: error ? `No, ${error}` : 'yes',
          });
        }
      }

      if (event.state === 'circleMode') {
        sarSelectedRef.current = true;
        // The map's bottom-left Height/Width readout only makes sense for a
        // bounded rectangle (Vantor FastView, Pleiades-Neo Guaranteed). The
        // circle pattern doesn't have meaningful per-axis dimensions, so
        // clear any stale values left over from a prior staticRectangleMode.
        // Also clear staticRectangleRef — calculateAoiHeightAndWidth reads it
        // unconditionally and would otherwise repopulate Height/Width on the
        // next 'update' event from the now-circle graphic.
        staticRectangleRef.current = null;
        setAoiHeightWidth({ height: null, width: null });

        // Leaving BlackSky point mode: the point graphic has no `.rings`/
        // centroid a Polygon can be built from, so it can't be the basis for
        // the new circle. originalPolygonGraphicRef still holds the last
        // real polygon drawn before BlackSky took over (pointMode's entry
        // handler calls saveOriginalPolygon before clearing the layer), so
        // fall back to that — same as every other mode conversion does.
        const isLeavingBlackSkyPoint = sketchVM.layer.graphics.some(
          (g: Graphic) => g.attributes?.blackskyPoint
        );
        if (isLeavingBlackSkyPoint) {
          sketchVM.layer.removeAll();
        }

        //checking if the polygon exists
        const graphic = (
          isLeavingBlackSkyPoint
            ? originalPolygonGraphicRef.current
            : sketchVM.layer.graphics.getItemAt(0)
        ) as Graphic & {
          geometry: Polygon;
        };

        if (graphic) {
          //this is to regenerate the center or centroid
          const oldPolygon = new Polygon({
            rings: graphic.geometry.rings,
            spatialReference: { wkid: 102100 },
          });
          const oldGraphic = new Graphic({
            geometry: oldPolygon,
            symbol: graphic.symbol,
            visible: false,
          });

          const centroid = oldPolygon.centroid;

          const longitude = centroid['longitude'];
          const latitude = centroid['latitude'];

          const { circleGraphics, centerPointGraphic, squarePolygon } =
            getCircleGraphicsAndSquarePolygonFromCenter({ x: longitude, y: latitude });

          sketchVM.layer.removeAll();

          //adding the graphics of previously drawn coordinates with visibility hidden
          // also adding it in zero index
          sketchVM.layer.add(oldGraphic);
          // adding it in 1 index after old graphics
          sketchVM.layer.add(circleGraphics);
          //also add the center graphics
          sketchVM.layer.add(centerPointGraphic);

          // only update the graphics through moving and also fire the update event to show area
          await sketchVM.update([circleGraphics, centerPointGraphic], { tool: 'move' });

          //setting polygon coordinates
          setPolygonCoordinates(squarePolygon.rings);
        }
      }

      if (event.state === 'staticRectangleMode') {
        staticRectangleRef.current = { width: event.width ?? 8, height: event.height ?? 8 };
        sketchVM.updateOnGraphicClick = false;
        // Mirrors circleMode clearing staticRectangleRef above: leaving SkySat
        // Assured (or BlackSky) for a buffer-type source never routes through
        // the polygonMode handler (the only other place this gets reset), so
        // without this, sarSelectedRef stays stuck `true` and the next
        // manually-drawn shape gets force-converted into a SAR circle again.
        sarSelectedRef.current = false;

        // Leaving BlackSky point mode: same reasoning as circleMode above —
        // fall back to originalPolygonGraphicRef (the last real polygon
        // drawn before BlackSky took over) instead of the un-convertible
        // point graphic.
        const isLeavingBlackSkyPoint = sketchVM.layer.graphics.some(
          (g: Graphic) => g.attributes?.blackskyPoint
        );
        if (isLeavingBlackSkyPoint) {
          sketchVM.layer.removeAll();
        }

        const graphic = (
          isLeavingBlackSkyPoint
            ? originalPolygonGraphicRef.current
            : sketchVM.layer.graphics.getItemAt(0)
        ) as Graphic & { geometry: Polygon };

        if (graphic) {
          saveOriginalPolygon(graphic);

          const existingPolygon = new Polygon({
            rings: graphic.geometry.rings,
            spatialReference: graphic.geometry.spatialReference ?? { wkid: 102100 },
          });
          const centroid = existingPolygon.centroid;
          const { width: fvW, height: fvH } = staticRectangleRef.current!;
          const {
            rectangleGraphic,
            centerPointGraphic: fvCenterPoint,
            rectanglePolygon,
          } = getRectangleFromCenter({ x: centroid.longitude, y: centroid.latitude }, fvW, fvH);
          sketchVM.cancel();
          sketchVM.layer.removeAll();
          sketchVM.layer.add(rectangleGraphic);
          sketchVM.layer.add(fvCenterPoint);
          await sketchVM.update([rectangleGraphic, fvCenterPoint], { tool: 'move' });
          setPolygonCoordinates(rectanglePolygon.rings);
        }
      }

      if (event.state === 'pointMode') {
        sketchVM.updateOnGraphicClick = false;
        // Same reset as staticRectangleMode above: leaving SkySat Assured
        // directly for BlackSky never routes through polygonMode either, so
        // sarSelectedRef must be cleared here too or it stays stuck `true`.
        sarSelectedRef.current = false;
        saveOriginalPolygon(
          sketchVM.layer.graphics.getItemAt(0) as (Graphic & { geometry: Polygon }) | null
        );
        sketchVM.layer.removeAll();

        // Keep the latest scene size around — it can change if the user
        // switches between BlackSky Gen-2 (33 sqkm) and Gen-3 (25 sqkm)
        // while a point is already drawn.
        if (event.areaSqkm) {
          blackskyAreaSqkmRef.current = event.areaSqkm;
        }

        // Restore the previously drawn point (if any) so the user can keep
        // editing it instead of starting over every time they switch back to
        // a BlackSky source.
        if (currentPointRef.current) {
          currentPointRef.current.visible = true;
          sketchVM.layer.add(currentPointRef.current);

          // Rebuilt fresh (not persisted) so it always reflects the
          // currently selected source's scene size.
          const centerPoint = currentPointRef.current.geometry as __esri.Point;
          const { circleGraphic, squarePolygon } = getBlackSkyPointCircleGraphic(
            { x: centerPoint.longitude, y: centerPoint.latitude },
            blackskyAreaSqkmRef.current ?? 25
          );
          currentPointCircleRef.current = circleGraphic;
          sketchVM.layer.add(circleGraphic);
          setPolygonCoordinates(squarePolygon.rings);

          await sketchVM.update([currentPointRef.current, circleGraphic], { tool: 'move' });
        }
        // else: layer stays empty until the user clicks the point button
      }

      if (event.state === 'polygonMode') {
        //code to remove circle
        sarSelectedRef.current = false;
        staticRectangleRef.current = null;
        sketchVM.updateOnGraphicClick = true;
        setAoiHeightWidth({ height: null, width: null });

        if (originalPolygonGraphicRef.current) {
          restoreOriginalPolygon();
          return;
        }

        const graphic = sketchVM.layer.graphics.getItemAt(0) as Graphic & {
          geometry: Polygon;
        };

        // Leaving BlackSky point mode: hide the graphics instead of destroying
        // them — currentPointRef/currentPointCircleRef keep them in memory so
        // they reappear if the user goes back to a BlackSky source. The layer
        // now holds both the point and its footprint circle, so this can't
        // just look at index 0.
        const isLeavingBlackSkyPoint = sketchVM.layer.graphics.some(
          (g: Graphic) => g.attributes?.blackskyPoint
        );
        if (isLeavingBlackSkyPoint) {
          sketchVM.cancel();
          sketchVM.layer.removeAll();
          // The bounding-square AOI belonged to the BlackSky point, not
          // whatever source comes next — clear it so it doesn't leak in.
          setPolygonCoordinates([[[]]]);
          return;
        }

        //if graphic exist and the first graphic is circle or static rectangle, clear it
        if (
          graphic &&
          (graphic.attributes?.circle ||
            graphic.attributes?.staticRectangle ||
            graphic.attributes?.staticRectangleCenter)
        ) {
          sketchVM.cancel();
          sketchVM.layer.removeAll();
          setPolygonCoordinates([[[]]]);
          return;
        }

        if (graphic) {
          //setting the old graphic visibility to true
          graphic.visible = true;
          sketchVM.layer.removeAll();

          sketchVM.layer.add(graphic);

          // fire the update start egraphicvent to show area in MapBox.tsx
          sketchVM.update(graphic);

          let coordinates = graphic.geometry?.rings;

          //if the geometry is type of map which is web mercator then change the coordinates
          if (graphic?.geometry?.spatialReference?.isWebMercator) {
            //creating new coordinates
            const ringsData = graphic.geometry.rings[0].map((value: any) => {
              return webMercatorUtils.xyToLngLat(value[0], value[1]);
            });
            coordinates = [ringsData];
          }
          //setting polygon coordinates
          setPolygonCoordinates(coordinates);
        }
      }
      if (event.state === 'clearPolygon') {
        setPolygonCoordinates([[[]]]);
        setAoiHeightWidth({ height: null, width: null });
        // The Clear button removes graphics directly (not via sketchVM.delete()),
        // so it never fires the 'delete' event that clears currentPointRef —
        // clear it here too or the BlackSky point comes back on re-entry.
        currentPointRef.current = null;
        currentPointCircleRef.current = null;
      }
    });

    const customEventCreate = sketchVM.on('create', async (event: TODO) => {
      const value = calculateAoiHeightAndWidth(event);
      setAoiHeightWidth({ width: value.width, height: value.height });

      if (event?.graphic?.geometry?.type === 'polyline') {
        if (event?.state !== 'cancel') {
          const polineGraphics = event?.graphic;
          polylineMeasurementToolEvent(polineGraphics);
        }

        if (event.state === 'cancel') {
          sketchVM.snappingOptions.featureSources.removeAll();
          if (polylineLabelRef.current) {
            sketchVM?.layer?.remove(polylineLabelRef.current);
            polylineLabelRef.current = null;
            measurementLayerRef.current = null;
          }
        }
      }

      if (event?.graphic?.geometry?.type === 'point') {
        if (event.state === 'complete') {
          // Tag and remember the graphic so pointMode can restore it later, and
          // switch straight to move-only editing — only 1 BlackSky point is
          // ever allowed at a time.
          event.graphic.attributes = { ...event.graphic.attributes, blackskyPoint: true };
          currentPointRef.current = event.graphic;

          // Draw the footprint circle (radius from the source's scene size)
          // around the point so the user sees the actual capture area, and
          // send the bounding square as the AOI polygon — everything past
          // this (offerings/coverage lookup, pricing, order submission) is
          // built around polygonCoordinates and only understands Polygon.
          const centerPoint = event.graphic.geometry as __esri.Point;
          const { circleGraphic, squarePolygon } = getBlackSkyPointCircleGraphic(
            { x: centerPoint.longitude, y: centerPoint.latitude },
            blackskyAreaSqkmRef.current ?? 25
          );
          currentPointCircleRef.current = circleGraphic;
          sketchVM.layer.add(circleGraphic);
          setPolygonCoordinates(squarePolygon.rings);

          await sketchVM.update([event.graphic, circleGraphic], { tool: 'move' });
        }
        return;
      }

      if (event.state === 'complete') {
        sketchVM.snappingOptions.featureSources.removeAll();
        if (event?.graphic?.geometry?.type === 'polyline') {
          sketchVM.snappingOptions.featureSources.removeAll();
          return;
        }
        let rings: any[][][] = event?.graphic?.geometry?.get('rings');

        const extent = event.graphic.geometry.extent;
        extentRef.current = extent;
        spatialReferenceRef.current = 102100;

        MixpanelTracking.getInstance().setProperty('ORDER_NEW_AOI_ERROR', 'aoi_mechanism', 'draw');

        //when intersecting between line polygon it creates two polygon
        if (event.graphic.geometry.get('isSelfIntersecting') || rings.length > 1) {
          customToaster(
            'Invalid Polygon',
            null,
            tabValue.current === K.TABS_VALUES.TASKING && {
              name: MixpanelEvent.ORDER_NEW_AOI_ERROR,
              properties: {
                aoi: rings,
                area: geometryEngine.geodesicArea(event.graphic.geometry, 'square-kilometers'),
                error: 'Invalid Polygon',
                aoi_mechanism: 'draw',
              },
            }
          );

          return;
        }

        const centroid: any = event.graphic.geometry.get('centroid');

        //longitude and latitude to send to the search location API
        const longitude = centroid['longitude'];
        const latitude = centroid['latitude'];

        //*************SAR code flow starts *********************/

        // use this to on/off the sarSelectedRef

        if (sarSelectedRef.current === true) {
          // sarSelectedRef is also flipped on by SkySat Assured (it reuses
          // the SAR circle UI), but the SAR-specific toast copy ("size and
          // orientation depend on the source and satellite orbit") is wrong
          // for SkySat Assured — that product is a fixed 25 km² footprint.
          // Vantor's static-rectangle path shows no toast either, so the
          // quiet default is consistent.
          if (!orderNewIsGuaranteedCapture?.current) {
            customToaster(t('sarInfoToast'), 'success');
          }
          const { circleGraphics, squarePolygon, centerPointGraphic } =
            getCircleGraphicsAndSquarePolygonFromCenter({
              x: longitude,
              y: latitude,
            });

          //change the incoming rings of the polygon to the bbox of the circle
          rings = squarePolygon.rings;

          const oldGraphic = new Graphic({
            geometry: event.graphic.geometry,
            symbol: event.graphic.symbol,
            visible: false,
          });

          sketchVM.layer.removeAll();
          //adding the oldGraphic at index 0;
          sketchVM.layer.add(oldGraphic);
          sketchVM.layer.add(circleGraphics);
          sketchVM.layer.add(centerPointGraphic);

          event.graphic = circleGraphics;
          //adding centerGraphic property for updation
          event.centerGraphic = centerPointGraphic;
        }

        //*********    SAR code flow code ends *************/

        //*************FastView code flow starts *********************/

        let fvCreateCenterPoint: any = null;
        if (staticRectangleRef.current) {
          const { width: fvW, height: fvH } = staticRectangleRef.current!;
          const {
            rectangleGraphic,
            centerPointGraphic: fvCenter,
            rectanglePolygon,
          } = getRectangleFromCenter({ x: longitude, y: latitude }, fvW, fvH);

          rings = rectanglePolygon.rings;
          fvCreateCenterPoint = fvCenter;

          if (!originalPolygonGraphicRef.current) {
            originalPolygonGraphicRef.current = event.graphic;
          }

          sketchVM.layer.removeAll();
          sketchVM.layer.add(rectangleGraphic);
          sketchVM.layer.add(fvCreateCenterPoint);
          event.graphic = rectangleGraphic;
        }

        //*********    FastView code flow ends *************/

        //convert the coordinate system to wkid 4326
        const ringsData = event?.graphic?.geometry?.spatialReference?.isWGS84
          ? rings[0]
          : rings[0].map((value: any) => {
              return webMercatorUtils.xyToLngLat(value[0], value[1]);
            });

        // setting states
        //if sar is true we set the coordinates of the rings
        //if it is false we just give default coordinates of the polygon
        setPolygonCoordinates(sarSelectedRef.current ? rings : [ringsData]);

        // 1st only update the graphics through moving and also fire the update event to show area
        // 2nd Conditin:selects the current polygon and fire the update start event to show area in MapBox.tsx
        sarSelectedRef.current
          ? sketchVM.update([event.graphic, event.centerGraphic], {
              tool: 'move',
            })
          : staticRectangleRef.current
            ? sketchVM.update([event.graphic, fvCreateCenterPoint], { tool: 'move' })
            : sketchVM.update(event.graphic);

        if (!checkArea(sarSelectedRef.current ? rings : [ringsData])) return;

        //if the Order New tab is active the search call will be avoided
        if (buttonRef.current?.getAttribute('aria-selected') === 'true') {
          if (
            // isArchiveNewDesignEnabledRef.current === false ||
            // isArchiveNewDesignEnabledRef.current === true &&
            isArchiveLandingScreenRef.current === false
          ) {
            getWebSocketPayload([ringsData]);
          }
        }

        view.goTo({ target: event.graphic.geometry.extent }, { duration: 4000 });
        //firing the update event to set area in MapBox

        //get coordinates for location
        // eslint-disable-next-line require-use-server
        const geoResult = await getLocationNameFromLongitudeAndLatitude(longitude, latitude);

        setSearchValue(geoResult?.address?.LongLabel || '');

        //tracking

        let polygonArea = geometryEngine.geodesicArea(event.graphic.geometry, 'square-kilometers');

        if (event.tool === 'polygon') {
          if (MixpanelTracking.getInstance().hasProperty('siderOrderNew') == true) {
            // get offering id, source and data type from cart items
            const eventData = getOfferingIdSourceAndDataType(cartItems);
            MixpanelTracking.getInstance().track(MixpanelEvent.ORDER_NEW_DRAW_POLYGON_AOI_DEFINED, {
              location: geoResult?.address?.LongLabel,
              coordinates: ringsData,
              area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
              offering_ids: eventData?.uniqueOfferingIds,
              sources: eventData?.uniqueSourceList,
              data_type: eventData?.uniqueDataTypes,
            });
          } else {
            // get offering id, source and data type from cart items
            const eventData = getOfferingIdSourceAndDataType(cartItems);
            MixpanelTracking.getInstance().track(
              MixpanelEvent.EXPLORE_EXISTING_DRAW_POLYGON_SEARCH,
              {
                location: geoResult?.address?.LongLabel,
                coordinates: ringsData,
                area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
                offering_ids: eventData?.uniqueOfferingIds,
                sources: eventData?.uniqueSourceList,
                data_type: eventData?.uniqueDataTypes,
              }
            );
          }
        }
        if (event.tool === 'rectangle') {
          if (MixpanelTracking.getInstance().hasProperty('siderOrderNew') == true) {
            // get offering id, source and data type from cart items
            const eventData = getOfferingIdSourceAndDataType(cartItems);
            MixpanelTracking.getInstance().track(
              MixpanelEvent.ORDER_NEW_DRAW_RECTANGLE_AOI_DEFINED,
              {
                location: geoResult?.address?.LongLabel,
                coordinates: ringsData,
                area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
                offering_ids: eventData?.uniqueOfferingIds,
                sources: eventData?.uniqueSourceList,
                data_type: eventData?.uniqueDataTypes,
              }
            );
          } else {
            // get offering id, source and data type from cart items
            const eventData = getOfferingIdSourceAndDataType(cartItems);
            MixpanelTracking.getInstance().track(
              MixpanelEvent.EXPLORE_EXISTING_DRAW_RECTANGLE_SEARCH,
              {
                location: geoResult?.address?.LongLabel,
                coordinates: ringsData,
                area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
                offering_ids: eventData?.uniqueOfferingIds,
                sources: eventData?.uniqueSourceList,
                data_type: eventData?.uniqueDataTypes,
              }
            );
          }
        }
      }
    });

    // Deleting the BlackSky point (e.g. pressing Delete/Backspace while it's
    // selected) must clear currentPointRef too, otherwise switching away and
    // back to BlackSky would resurrect the graphic the user just removed.
    const customEventDelete = sketchVM.on('delete', (event: TODO) => {
      const deletedPoint = event?.graphics?.some(
        (graphic: Graphic) => graphic.attributes?.blackskyPoint
      );
      if (deletedPoint) {
        currentPointRef.current = null;
        currentPointCircleRef.current = null;
      }
    });

    if (!view) return;
    //check if any graphic exist on clicking
    const viewClickEvent = view.on('click', async function (event: TODO) {
      // the hitTest() checks to see if any graphics are in the view

      const graphics = await view.hitTest(event);
      //check if any graphic exist on clicking
      if (graphics?.results && graphics?.results.length > 0) {
        const result = graphics.results;

        // get the graphic layer of sketchVM in which the title is graphicsLayer
        const filterGraphics = result.filter(value => value.layer?.title === 'graphicsLayer');

        if (filterGraphics.length > 0 && filterGraphics[0].type === 'graphic') {
          //if the graphic selection is circle then just make the tool effective
          if (filterGraphics[0].graphic?.attributes?.circle) {
            //getting the circle center graphics
            const centerGraphic = sketchVM.layer.graphics.at(2);
            await sketchVM.update([filterGraphics[0].graphic, centerGraphic], {
              tool: 'move',
            });
          } else if (
            filterGraphics[0].graphic?.attributes?.staticRectangle ||
            filterGraphics[0].graphic?.attributes?.staticRectangleCenter
          ) {
            // Static rectangle is move-only — re-establish move session with both graphics
            const rectGraphic = sketchVM.layer.graphics.getItemAt(0);
            const centerGraphic = sketchVM.layer.graphics.getItemAt(1);
            await sketchVM.update([rectGraphic, centerGraphic], { tool: 'move' });
          } else await sketchVM.update(filterGraphics[0].graphic);
        }
        //unselect only if there is highlight graphics

        // Find the clustered drone GeoJson Layer
        const orderAbleGeoJsonLayer = graphics?.results?.find(
          (r): r is __esri.GraphicHit =>
            'graphic' in r && r?.graphic?.layer?.title === 'orderableCenterGeoJsonLayer'
        );

        const isClustered = orderAbleGeoJsonLayer?.graphic?.attributes?.cluster_count > 1;

        // Unclustered the Layer one by one
        if (isClustered) {
          view &&
            view.goTo(
              { target: orderAbleGeoJsonLayer?.graphic?.geometry, zoom: view.zoom + 2 },
              { duration: 2000 }
            );
        }

        // If there is only one symbol left on the screen make it into original GeoJSon

        if (orderAbleGeoJsonLayer && !isClustered) {
          view &&
            view.goTo(
              { target: orderAbleGeoJsonLayer?.graphic?.geometry, zoom: 10 },
              { duration: 2000 }
            );
        }
      }
    });

    return () => {
      customEventUpdate.remove();
      customEventUpdate2.remove();
      customEventCreate.remove();
      customEventDelete.remove();

      viewClickEvent && viewClickEvent.remove();
      setMultiProduct(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sketchVM, view, filterStartDate, filterLastDate]);

  useEffect(() => {
    //load map data from search params
    try {
      if (view && sketchVM && isPosthogFlagLoaded)
        loadPolygonDataFromSearchParams(view, sketchVM, searchData);
    } catch (error) {
      customToaster('Error loading data');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, sketchVM, searchData, isPosthogFlagLoaded]);

  useEffect(() => {
    if (polygonCoordinates[0][0].length > 0) {
      intersectLayer.removeAll();
      previewLayer?.source?.elements?.removeAll();

      if (isArchiveLandingScreenRef.current === false) {
        getWebSocketPayload(polygonCoordinates);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStartDate, filterLastDate, selectDataType]);

  // AI agent event handlers (search, refine, cart) — extracted to feature module
  useAgentSiderEvents({
    setIsArchiveLandingScreen,
    isArchiveLandingScreenRef,
    setSearchValue,
    setSelectDataType,
    selectDataTypeRef,
    setValue,
    setFilterStartDate,
    setFilterLastDate,
    setPolygonCoordinates,
    polygonCoordinates,
    sketchVM,
    view,
    previewLayer,
    intersectLayer,
    getWebSocketPayload,
    products,
    cartItems,
    increaseCartQuantity,
    removeFromCart,
    resetCart,
    openCart,
  });

  const currentTab = searchData?.tab;
  useEffect(() => {
    if (currentTab && currentTab === K.TABS_VALUES.TASKING) {
      setIsNew(K.TABS_VALUES.TASKING);
      tabValue.current = K.TABS_VALUES.TASKING;
      setTabButtonValue(K.TABS_VALUES.TASKING);

      MixpanelTracking.getInstance().setProperty('siderOrderNew', 'active', true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const removeLayerAndHighlightedCard = useCallback(() => {
    intersectLayer.removeAll();
    previewLayer.source.elements.removeAll();
    const activeClasses = productCardContainerRef.current?.querySelectorAll('.active');
    activeClasses?.forEach(card => {
      card.classList.remove('active');
    });
  }, [intersectLayer, previewLayer]);
  //assigning the callback
  removeAndHighlightLayerCbRef.current = removeLayerAndHighlightedCard;

  function calculateRank(resolution: number, coverage: number, captureDate: Date, endDate: Date) {
    if (isNaN(captureDate.getTime()) || isNaN(endDate.getTime())) {
      customToaster('Invalid date format');
      return 0;
    }

    const differenceInDays = (endDate.getTime() - captureDate.getTime()) / (24 * 60 * 60 * 1000);

    const numericResolution = resolution;

    return numericResolution * (-1 * differenceInDays + 48 * coverage);
  }

  function sortFeatures(features: ProductCardType[], endDate: Date) {
    return features.sort((a, b) => {
      const rankA = calculateRank(a.resolution, a.locationCoverage, new Date(a.startDate), endDate);

      const rankB = calculateRank(b.resolution, b.locationCoverage, new Date(b.startDate), endDate);

      return rankB - rankA;
    });
  }

  function checkArea(coordinates: number[][][]) {
    let area = geometryEngine.geodesicArea(
      new Polygon({ rings: coordinates, spatialReference: { wkid: 4326 } }),
      'square-kilometers'
    );

    if (area < 0) area *= -1;

    if (!isAllowMaxAoiSize && area > 5000 && tabValue.current === K.TABS_VALUES.ARCHIVE) {
      MixpanelTracking.getInstance().setProperty(
        'savedAreasFileUpload',
        'error',
        'Selected area must be less than 5000km\u00B2'
      );
      customToaster(t('selectAreaLesser'), 'Selected area must be less than 5000km²');

      return false;
    } else if (tabValue.current === K.TABS_VALUES.ARCHIVE && area < 0.1) {
      customToaster(t('selectAreaGreaterThanOneKilo', 'Selected area must be greater than 0.1km²'));
      return false;
    }

    return true;
  }

  function getWebSocketPayload(coordinates: number[][][]) {
    const newCoordinates = coordinates;
    if (!checkArea(coordinates)) return;

    const startDate = getValues('startDate');
    const lastDate = getValues('lastDate');

    if (!moment(lastDate).isAfter(startDate)) {
      return customToaster(t('common:selectDateRange'));
    }

    if (
      webSocketRef.current &&
      (webSocketRef.current.readyState === 1 || webSocketRef.current.readyState === 0)
    ) {
      webSocketRef.current.close();
    }

    const ws = new WebSocket(`${process.env.NEXT_PUBLIC_WEB_SOCKET_URL}`);

    setShowSkeleton(true);
    SetProducts([]);

    // Reset merge option on search
    setMultiProduct(false);
    mergeOverlapRef.current = false;
    disableSrcRef.current = '';
    disableBandsRef.current = '';
    disableConstellationRef.current = '';
    mergeOverlapPrimaryItemRef.current = false;
    multiProductCartItemRef.current = {};

    // removing map options
    previewLayer?.source?.elements?.removeAll();

    // mergeOverlapItemRef.current = [];

    ws.onopen = async event => {
      try {
        let token = await getAuthenticatedToken();
        if (!token) {
          // eslint-disable-next-line require-use-server
          token = await getGuestToken();
        }
        const valueForAllOption = landingScreenArchiveDTOptions
          ?.map((item: any) => item?.value)
          ?.filter(item => item !== 'all');
        const payloadData = {
          action: 'connect',
          config: {
            location: {
              coordinates: coordinates,
              type: 'Polygon',
            },
            start_date: filterStartDate,
            end_date: filterLastDate,
            coverage: 30,
            ...(selectDataTypeRef.current?.length > 0 &&
              !selectDataTypeRef.current?.includes('all') && {
                sensor_type: selectDataTypeRef.current,
              }),
            // resolution: ['low', 'medium', 'high', 'very_high'],
            resolution_high: 0.01,
            resolution_low: 30,
          },
          JWT_TOKEN: `Bearer ${token}`,
        };

        const payloadDataString = JSON.stringify(payloadData);

        ws.send(payloadDataString);
        setProgress(0);

        if (ws.readyState != 3) {
          SetIsFetchingData(true);
          // setPlaceholder(false);
        }
      } catch (error) {
        console.log('websocket:ERROR', error);
      }
    };

    let loadingData = 0;
    let progressBarData = [];
    const polygonA = new Polygon({ rings: coordinates, spatialReference: { wkid: 4326 } });
    const originalSearchedArea = geometryEngine.geodesicArea(polygonA, 'square-kilometers');
    const productData: ProductCardType[] = [];

    const getOutputId = (sensorType: string, isPanchromatic: boolean | null) => {
      if (isPanchromatic) return imageTypes.PANCHROMATIC_VALUE;

      // From CD-1063 both Precision 3D and WorldDEM has sensor type of Elevation
      if (sensorType === 'Elevation') return imageTypes.ELEVATION_VALUE;

      return imageTypes.ALL_OPTICAL_BANDS_VALUE;
    };
    ws.onmessage = async event => {
      const dataJSON = JSON.parse(event.data);

      if (dataJSON['message'] === 'error') {
        const errorData: { message: string }[] = dataJSON['data']['errors'];
        errorData?.forEach(value => {
          value?.message && customToaster(`${value?.message}`);
        });

        return;
      }

      if (dataJSON['message'] === 'progress_bar_update') {
        progressBarData.push(dataJSON);

        setProgress((progressBarData.length * 100) / 9);
        if (progressBarData.length === 9) {
          SetIsFetchingData(false);
          setShowSkeleton(false);
        }
      }

      if (dataJSON['message'] === 'search_results') {
        //this condition is to run one time

        const MapData: WebSocketData[] = JSON.parse(dataJSON['data']);
        const productData: ProductCardType[] = [];
        MapData.forEach(data => {
          const formattedDate = moment(data['start_time']).format('MMM D, YYYY');
          const startDate = data['start_time'];
          const archiveEndDate = data['end_time'];

          const price = data['cost'];

          const source = data['source'];
          const resolution = data['resolution'];
          const cloudCoverage = roundOffUptoTwo(data['result_cloud_cover_percentage']);
          const areaCoverage =
            !data['location_coverage_percentage'] || data['location_coverage_percentage'] === ''
              ? 0
              : Math.round(
                  typeof data['location_coverage_percentage'] === 'string'
                    ? Number(data['location_coverage_percentage'])
                    : data['location_coverage_percentage']
                );

          const resolutionForFilter = formatResolutionValueForFilter(resolution);
          const cloudCoverageForFilter = formatAreaAndCloudCoverageForFilter(cloudCoverage);
          const areaCoverageForFilter = formatAreaAndCloudCoverageForFilter(areaCoverage);
          const originalSearchedCoordinates = newCoordinates;
          const valuesForFilters = {
            resolution: resolutionForFilter,
            cloudCoverage: cloudCoverageForFilter,
            areaCoverage: areaCoverageForFilter,
          };
          const productId = data?.product?.id ?? null;
          const locationCoverage =
            typeof data['location_coverage_percentage'] === 'string'
              ? Number(data['location_coverage_percentage'])
              : data['location_coverage_percentage'];

          const sensorType = data['sensor_type'];
          const thumbnailImg = data['thumbnail_uri'];
          const previewImg = data['preview_uri'];
          const coordinates = data['location']['coordinates'];
          const intersectionCoordinates = data['intersection']
            ? data['intersection']['coordinates']
            : null;
          const pricePerKm = data['price_per_sq_km'];
          const area = data['area_sq_km'];

          const productName = data['product_name'];
          const minimumArea = data['minimum_area_sq_km'];
          const archiveOfferingId = data?.product?.configuration?.offering_id ?? null;
          const bandsLength = data?.properties ? data?.properties?.bands?.length : null;
          const bands = data?.properties ? data?.properties?.bands : null;
          const constellation = data?.properties ? data?.properties?.constellation : null;
          const searchId = data['search_id'];
          const bbox = data['location']['bbox'];
          const offNadirAngle = data['off_nadir_angle'] ? Math.abs(data['off_nadir_angle']) : 0;
          const isSensorElevation = data?.sensor_type === 'Elevation';
          const originalData = data;
          const bundleName = isSensorElevation
            ? formatBundleName(data?.product?.configuration?.offering_name)
            : null;
          const isPanchromatic = checkPanchromatic({
            source: source,
            bandsLength: bandsLength,
            bands: bands,
            constellation: constellation,
          });

          const outputId = getOutputId(sensorType, isPanchromatic);
          productData.push({
            id: data['id'],
            date: formattedDate,
            archiveEndDate,
            outputId,
            price,
            valuesForFilters,
            source,
            pricePerKm,
            isSensorElevation,
            bundleName,
            productId,
            area,
            archiveOfferingId,
            resolution,
            originalSearchedCoordinates,
            bandsLength,
            bands,
            constellation,
            thumbnailImg,
            coordinates,
            productName,
            cloudCoverage,
            isPanchromatic,
            areaCoverage,
            previewImg,
            sensorType,
            searchId,
            minimumArea,
            locationCoverage,
            startDate,
            originalData,
            bbox,
            originalSearchedArea: Math.abs(originalSearchedArea),
            offNadirAngle,
            intersectionCoordinates,
          });
          //for tracking
          MixpanelTracking.getInstance().setProperty('currentSearchId', 'searchId', searchId);
        });

        const sortedResult = sortFeatures(productData, calculatedEndDate);
        SetProducts(p => {
          return [...p, ...sortedResult];
        });
      }
    };
    ws.onclose = event => {
      if (productData?.length === 0 && !isFetchingData) {
        setPlaceholder(true);
      }

      SetIsFetchingData(false);

      if (event.code !== 1006 && event.code !== 1005) setShowSkeleton(false);
    };

    ws.onerror = event => {
      // this line is commented due to CD-1057
      // setShowSkeleton(false);
      // customToaster(`Websocket error`);

      console.log('websocket error: ', event);
      ws.close();
    };

    webSocketRef.current = ws;
  }

  function validateGeoFile(geoFile: any): boolean {
    let error = null;

    if (geoFile['type'] === 'FeatureCollection') {
      // file format validation
      switch (true) {
        // if feature exist
        case !geoFile['features'] || geoFile['features']?.length === 0:
          error = t('provider:noFeature');
          break;

        // Single Feature allowed
        case geoFile['features'].length > 1:
          error = t('provider:singleFeatureAllowed');
          break;

        // Single Feature should be an object
        case Array.isArray(geoFile['features'][0]):
          error = t('provider:referGeoJson');
          break;

        // if polygon type exist
        case !geoFile['features'][0]['geometry']:
          error = t('provider:noGeometryKey');
          break;

        // if polygon type exist
        case !geoFile['features'][0]['geometry']['type']:
          error = t('provider:noGeometryType');
          break;
        // only type polygon is selected
        case geoFile['features'][0]['geometry']['type'] !== 'Polygon':
          error = t('provider:noPolygon');
          break;

        // check if coordinates exist
        case !geoFile['features'][0]['geometry']['coordinates']:
          error = `${t('provider:noCoordinate')}:[[ [lon,lat], [lon,lat], [lon,lat], [lon,lat] ]]`;
          break;

        //check coordinates are valid
        case !geoFile['features'][0]['geometry']['coordinates'][0]:
          error = `${t('provider:invalidCoordinate')}:[[ [lon,lat], [lon,lat], [lon,lat], [lon,lat] ]]`;
          break;

        // check coordinates are valid
        case !geoFile['features'][0]['geometry']['coordinates'][0][0]:
          error = `${t('provider:invalidCoordinate')}:[[ [lon,lat], [lon,lat], [lon,lat], [lon,lat] ]]`;
          break;

        // check if coordinates provided is not less than 4
        case geoFile['features'][0]['geometry']['coordinates'][0].length < 4:
          error = t('provider:minimumPolygon');
          break;
        default:
          error = null;
          break;
      }

      if (error) {
        MixpanelTracking.getInstance().setProperty('savedAreasFileUpload', 'error', error);
        customToaster(
          error,
          null,
          tabValue.current === K.TABS_VALUES.TASKING && {
            name: MixpanelEvent.ORDER_NEW_AOI_ERROR,
            properties: {
              aoi: null,
              area: null,
              error,
              aoi_mechanism: 'upload',
            },
          }
        );
        return false;
      }
      const coordinates: number[][][] = geoFile['features'][0]['geometry']['coordinates'];
      let invalidCoordinates = false;
      let invalidCoorMsg = '';
      // Only valid projection is accepted value
      coordinates[0].forEach((value, index) => {
        //first is longitude ranges from -180 to 180
        if (!(value[0] >= -180 && value[0] <= 180)) {
          invalidCoordinates = true;
          invalidCoorMsg += `${'provider:invalidLongitude'} ${value[0]} ${t('provider:in')} [${value}] ${t('provider:atIndex')} ${index + 1}`;
          customToaster(
            `${'provider:invalidLongitude'} ${value[0]} ${t('provider:in')} [${value}] ${t('provider:atIndex')} ${index + 1}`
          );
        }

        //Second is latitude ranges from -90 to 90
        if (!(value[1] >= -90 && value[1] <= 90)) {
          invalidCoordinates = true;
          invalidCoorMsg += `${t('provider:invalidLatitude')} ${value[1]} ${t('provider:in')} [${value}] ${t('provider:atIndex')} ${index + 1}`;
          customToaster(
            `${t('provider:invalidLatitude')} ${value[1]} ${t('provider:in')} [${value}] ${t('provider:atIndex')} ${index + 1} `
          );
        }
      });

      if (invalidCoordinates) {
        MixpanelTracking.getInstance().track(MixpanelEvent.ORDER_NEW_AOI_ERROR, {
          aoi: coordinates,
          area: null,
          error: invalidCoorMsg,
          aoi_mechanism: 'upload',
        });
        return false;
      }

      // creating polygon for checking the self intersection
      // for open and close geometry polygon automatically creates it closed
      const polygon = new Polygon({ rings: coordinates, spatialReference: { wkid: 4326 } });

      if (polygon.isSelfIntersecting) {
        customToaster(
          t('provider:invalidPolygonError'),
          null,
          tabValue.current === K.TABS_VALUES.TASKING && {
            name: MixpanelEvent.ORDER_NEW_AOI_ERROR,
            properties: {
              aoi: coordinates,
              area: geometryEngine.geodesicArea(polygon, 'square-kilometers'),
              error: t('provider:invalidPolygonError'),
              aoi_mechanism: 'upload',
            },
          }
        );

        return false;
      }
    } else if (geoFile['type'] === 'Feature') {
      switch (true) {
        // if feature exist

        // if polygon type exist
        case !geoFile['geometry']:
          error = t('provider:noGeometryKey');
          break;

        // if polygon type exist
        case !geoFile['geometry']['type']:
          error = t('provider:noGeometryType');
          break;
        // only type polygon is selected
        case geoFile['geometry']['type'] !== 'Polygon':
          error = t('provider:onlyPolygon');
          break;

        // check if coordinates exist
        case !geoFile['geometry']['coordinates']:
          error = `${t('provider:noCoordinate')}:[[ [lon,lat], [lon,lat], [lon,lat], [lon,lat] ]]`;
          break;

        //check coordinates are valid
        case !geoFile['geometry']['coordinates'][0]:
          error = `${t('provider:invalidCoordinate')}:[[ [lon,lat], [lon,lat], [lon,lat], [lon,lat] ]]`;
          break;

        // check coordinates are valid
        case !geoFile['geometry']['coordinates'][0][0]:
          error = `${t('provider:invalidCoordinate')}:[[ [lon,lat], [lon,lat], [lon,lat], [lon,lat] ]]`;
          break;

        // check if coordinates provided is not less than 4
        case geoFile['geometry']['coordinates'][0].length < 4:
          error = t('provider:minimumPolygon');
          break;
        default:
          error = null;
          break;
      }

      if (error) {
        customToaster(
          error,
          null,
          tabValue.current === K.TABS_VALUES.TASKING && {
            name: MixpanelEvent.ORDER_NEW_AOI_ERROR,
            properties: {
              aoi: null,
              area: null,
              error,
              aoi_mechanism: 'upload',
            },
          }
        );
        return false;
      }

      const coordinates: number[][][] = geoFile['geometry']['coordinates'];
      let invalidCoordinates = false;
      // Only valid projection is accepted value
      coordinates[0].forEach((value, index) => {
        //first is longitude ranges from -180 to 180
        if (!(value[0] >= -180 && value[0] <= 180)) {
          invalidCoordinates = true;

          customToaster(
            `${t('provider:invalidLongitude')} ${value[0]} ${t('provider:in')} [${value}] ${t('provider:atIndex')} ${index + 1}`
          );
        }

        //Second is latitude ranges from -90 to 90
        if (!(value[1] >= -90 && value[1] <= 90)) {
          invalidCoordinates = true;

          customToaster(
            `${t('provider:invalidLatitude')} ${value[1]} ${t('provider:in')} [${value}] ${t('provider:atIndex')} ${index + 1} `
          );
        }
      });

      if (invalidCoordinates) {
        return false;
      }

      // creating polygon for checking the self intersection
      // for open and close geometry polygon automatically creates it closed
      const polygon = new Polygon({ rings: coordinates, spatialReference: { wkid: 4326 } });

      if (polygon.isSelfIntersecting) {
        customToaster(
          t('provider:invalidPolygonError'),
          null,
          tabValue.current === K.TABS_VALUES.TASKING && {
            name: MixpanelEvent.ORDER_NEW_AOI_ERROR,
            properties: {
              aoi: coordinates,
              area: geometryEngine.geodesicArea(polygon, 'square-kilometers'),
              error: t('provider:invalidPolygonError'),
              aoi_mechanism: 'upload',
            },
          }
        );

        return false;
      }
    } else {
      customToaster(
        t('provider:typeNotSupported'),
        null,
        tabValue.current === K.TABS_VALUES.TASKING && {
          name: MixpanelEvent.ORDER_NEW_AOI_ERROR,
          properties: {
            aoi: null,
            area: null,
            error: t('provider:typeNotSupported'),
            aoi_mechanism: 'upload',
          },
        }
      );
      return false;
    }

    // -- Need to see Simplified vertices

    return true;
  }

  // <TabList> handlers
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabButtonValue(newValue);
    //if the Order New is selected
    const polygon = new Polygon({ rings: polygonCoordinates, spatialReference: { wkid: 4326 } });
    const area = geometryEngine.geodesicArea(polygon, 'square-kilometers');
    if (newValue === K.TABS_VALUES.TASKING) {
      //clear or remove the old graphic
      intersectLayer?.removeAll();
      // remove the previous preview images
      previewLayer?.source?.elements?.removeAll();
      // !! changes required here
      sketchVM?.emit('update', { state: 'orderNew', show: true, area: Math.abs(area) });
      MixpanelTracking.getInstance().setProperty('siderOrderNew', 'active', true);

      //if the explore Existing is selected
    } else {
      //  use case: When we navigate the user to tasking tab using url parameter and user clicks to archive tab since aoi has been created in tasking tab, we need to get the aoi coordinates from url parameters and pass it to the WebSocket to initiate the websocket connection.

      // url Parameters check for WebSocket to initiate
      // 1. isLandingScreen is true
      // 2. products length is 0
      // 3. searchData tab is TASKING
      // 4. searchData coordinates length is greater than 0

      if (
        isArchiveLandingScreenRef.current === true &&
        products?.length === 0 &&
        searchData?.tab === K.TABS_VALUES.TASKING &&
        searchData?.coordinates?.length > 0
      ) {
        getWebSocketPayload(searchData?.coordinates);
      }

      getUiDataConditionBased();
      sketchVM?.emit('update', { state: 'orderNew', show: false, area: Math.abs(area) });
      sketchVM?.emit('update', { state: 'polygonMode' });
      MixpanelTracking.getInstance().clearProperty('siderOrderNew');
    }
    tabValue.current = newValue;
    setIsNew(newValue);

    if (newValue === K.TABS_VALUES.ARCHIVE) {
      MixpanelTracking.getInstance().track(MixpanelEvent.ORDER_IMAGE_EXPLORE_EXISTING_CLICK);
    } else {
      MixpanelTracking.getInstance().track(MixpanelEvent.ORDER_IMAGE_ORDER_NEW_CLICK);
    }
  };

  // <UploadDialog /> handlers
  const handleFileUpload = async (file: File) => {
    const fileReader = new FileReader();
    try {
      MixpanelTracking.getInstance().setProperty('ORDER_NEW_AOI_ERROR', 'aoi_mechanism', 'upload');
      // valid polygon coordinates
      // valid geojson format
      // only 1 polygon is allowed
      // const fileReader = new FileReader();

      // getting the filename
      // const filename = e.target.files[0].name;
      const filename = file.name;

      //getting the file extension
      const ext = /[^.]+$/.exec(filename);

      if (!ext) return fileReader;

      //checking the file extension
      const filetype = fileExt[ext[0]];

      //throw error here for invalid file extension
      if (!filetype) {
        MixpanelTracking.getInstance().setProperty(
          'savedAreasFileUpload',
          'error',
          'Invalid format. Only .json, .geojson and .kml file is allowed'
        );
        customToaster(
          t('provider:invalidFormat'),
          null,
          tabValue.current === K.TABS_VALUES.TASKING && {
            name: MixpanelEvent.ORDER_NEW_AOI_ERROR,
            properties: {
              aoi: null,
              area: null,
              error: t('provider:invalidFormat'),
              aoi_mechanism: 'upload',
            },
          }
        );

        return fileReader;
      }

      //setting the file Extension in mixpanel
      MixpanelTracking.getInstance().setProperty('file', 'ext', filetype);

      // fileReader.readAsText(e.target.files[0], 'UTF-8');

      fileReader.readAsText(file, 'UTF-8');

      fileReader.onload = async e => {
        const result = e.target?.result?.toString();
        let geoFile;

        try {
          // if file type is kml convert it to geojson
          if (filetype === 'kml') {
            var kml = result && new DOMParser().parseFromString(result, 'application/xhtml+xml');
            geoFile = tj.kml(kml);
          } else {
            geoFile = result && JSON.parse(result?.toString());
          }
        } catch (err) {
          MixpanelTracking.getInstance().setProperty(
            'savedAreasFileUpload',
            'error',
            `Invalid File Content: ${err}`
          );
          customToaster(
            `${t('provider:invalidFileContent')} ${err}`,
            null,
            tabValue.current === K.TABS_VALUES.TASKING && {
              name: MixpanelEvent.ORDER_NEW_AOI_ERROR,
              properties: {
                aoi: null,
                area: null,
                error: `${t('provider:invalidFileContent')} ${err}`,
                aoi_mechanism: 'upload',
              },
            }
          );

          return;
        }

        if (!validateGeoFile(geoFile)) {
          customToaster(
            t('provider:invalidFileContent'),
            null,
            tabValue.current === K.TABS_VALUES.TASKING && {
              name: MixpanelEvent.ORDER_NEW_AOI_ERROR,
              properties: {
                aoi: null,
                area: null,
                error: t('provider:invalidFileContent'),
                aoi_mechanism: 'upload',
              },
            }
          );
          return;
        }

        let coordinates = null;

        if (geoFile['type'] === 'FeatureCollection')
          coordinates = geoFile['features'][0]['geometry']['coordinates'];

        if (geoFile['type'] === 'Feature') coordinates = geoFile['geometry']['coordinates'];

        const polygon = new Polygon({ rings: coordinates, spatialReference: { wkid: 4326 } });

        let squarePolygon: any = null;
        let circleGraphics: any = null;
        let centerPointGraphics: any = null;
        //if sar flow is active
        if (sarSelectedRef.current === true) {
          // sarSelectedRef is also flipped on by SkySat Assured (which
          // reuses the SAR circle UI). Suppress the SAR-specific toast
          // in that case — same gating as the customEventCreate draw
          // path above; this branch fires for file-upload / programmatic
          // AOI definitions.
          if (!orderNewIsGuaranteedCapture?.current) {
            customToaster(t('sarInfoToast'), 'success');
          }
          let result = getCircleGraphicsAndSquarePolygonFromCenter({
            x: polygon.centroid.x,
            y: polygon.centroid.y,
          });
          squarePolygon = result.squarePolygon;
          circleGraphics = result.circleGraphics as Graphic;
          centerPointGraphics = result.centerPointGraphic;
        }
        let staticRectanglePolygon: any = null;
        let staticRectangleGraphic: any = null;
        let staticRectangleCenterGraphic: any = null;
        if (staticRectangleRef.current) {
          const { width: fvW, height: fvH } = staticRectangleRef.current!;
          const result = getRectangleFromCenter(
            { x: polygon.centroid.x, y: polygon.centroid.y },
            fvW,
            fvH
          );
          staticRectanglePolygon = result.rectanglePolygon;
          staticRectangleGraphic = result.rectangleGraphic;
          staticRectangleCenterGraphic = result.centerPointGraphic;
        }

        const isFastview = !!staticRectangleRef.current;
        const isSar = sarSelectedRef.current === true;

        // creating a graphic and showing it to a map
        const graphic = new Graphic({
          geometry: polygon,
          symbol: sketchVM?.polygonSymbol.clone(),
          visible: !isSar && !isFastview,
        });
        extentRef.current = polygon?.extent;
        spatialReferenceRef.current = 4326;

        //removing graphics
        previewLayer.source.elements.removeAll();
        sketchVM.layer.removeAll();
        intersectLayer.removeAll();
        //clearing the intersection area value
        sketchVM.emit('update', { state: 'clearIntersectArea' });

        //if the sar flow is active we are targeting the previous polygon to store in zero index
        sketchVM.layer.add(graphic);
        //if the sar flow is active add the circle graphics
        // sarSelectedRef.current && sketchVM.layer.add(circleGraphics),
        if (isSar) {
          sketchVM.layer.add(circleGraphics);
          sketchVM.layer.add(centerPointGraphics);
        } else if (isFastview) {
          sketchVM.layer.add(staticRectangleGraphic);
          sketchVM.layer.add(staticRectangleCenterGraphic);
        }

        //firing the update event to set area in MapBox
        if (isSar) {
          sketchVM.update([circleGraphics, centerPointGraphics], { tool: 'move' });
        } else if (isFastview) {
          sketchVM.update([staticRectangleGraphic, staticRectangleCenterGraphic], { tool: 'move' });
        } else {
          sketchVM.update(graphic);
        }

        view.goTo({ target: polygon.extent }, { duration: 4000 });

        // setting polygon coordinates and other states
        const finalCoordinates = isSar
          ? squarePolygon?.rings
          : isFastview
            ? staticRectanglePolygon?.rings
            : coordinates;

        setPolygonCoordinates(finalCoordinates);

        if (!checkArea(finalCoordinates)) return;
        //if the Order New tab is active the search call will be avoided
        if (buttonRef.current?.getAttribute('aria-selected') === 'true') {
          if (isArchiveLandingScreenRef.current === false) {
            getWebSocketPayload(coordinates);
          }
        }

        const latitude = polygon.centroid.latitude;
        const longitude = polygon.centroid.longitude;

        //reverse geocode url which accepts longitude and latitude and return the address in words only
        // eslint-disable-next-line require-use-server
        const geoResult = await getLocationNameFromLongitudeAndLatitude(longitude, latitude);

        setSearchValue(geoResult?.address?.LongLabel || '');

        //for tracking
        let polygonArea = geometryEngine.geodesicArea(polygon, 'square-kilometers');

        // get offering id, source and data type from cart items
        const eventData = getOfferingIdSourceAndDataType(cartItems);
        if (MixpanelTracking.getInstance().hasProperty('siderOrderNew') == true) {
          MixpanelTracking.getInstance().track(MixpanelEvent.ORDER_NEW_UPLOAD_FILE_AOI_DEFINED, {
            file_extension: filetype,
            location: geoResult?.address?.LongLabel,
            coordinates: coordinates[0],
            area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
            offering_ids: eventData?.uniqueOfferingIds,
            sources: eventData?.uniqueSourceList,
            data_type: eventData?.uniqueDataTypes,
          });
        } else {
          MixpanelTracking.getInstance().track(MixpanelEvent.EXPLORE_EXISTING_UPLOAD_FILE_SEARCH, {
            file_extension: filetype,
            location: geoResult?.address?.LongLabel,
            coordinates: coordinates[0],
            area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
            offering_ids: eventData?.uniqueOfferingIds,
            sources: eventData?.uniqueSourceList,
            data_type: eventData?.uniqueDataTypes,
          });
        }
      };

      return fileReader;
    } catch (err) {
      return fileReader;
    }
  };

  // <AutoComplete /> handlers
  const handleSelection = async (selectedItem: any) => {
    if (!selectedItem) return;

    setSearchValue(selectedItem?.label || '');
    try {
      // const geoResult = await getBoxCoordinatesFromSuggestionMagicKey(selectedItem.magicKey);
      const geoResult = await fetch(
        `${process.env.NEXT_PUBLIC_FIND_ADDRESS_URL}?` +
          new URLSearchParams({
            token: `${process.env.NEXT_PUBLIC_ESRI_MAP_CONFIG_KEY}`,
            magicKey: selectedItem?.magicKey,
            langCode: 'en',
            f: 'json',
          })
      ).then(async value => {
        let result = await value.json();
        return result;
      });
      //fetching suggestion data with magic key

      if (geoResult?.error) {
        customToaster(t('common:generalError'));
        return;
      }

      const extent = geoResult.candidates[0].extent;

      var bbox = [
        [
          [extent.xmin, extent.ymax],
          [extent.xmax, extent.ymax],
          [extent.xmax, extent.ymin],
          [extent.xmin, extent.ymin],
          [extent.xmin, extent.ymax],
        ],
      ];

      let squarePolygon: any = null;
      let circleGraphics: any = null;
      let centerPointGraphics: any = null;
      //if sar flow is active
      if (sarSelectedRef.current === true) {
        // sarSelectedRef is also flipped on by SkySat Assured (which
        // reuses the SAR circle UI). Suppress the SAR-specific toast in
        // that case — same gating as the customEventCreate draw path;
        // this branch fires for address-search / geocoder-driven AOI.
        if (!orderNewIsGuaranteedCapture?.current) {
          customToaster(t('sarInfoToast'), 'success');
        }
        //object is location: {x: num,y:num}
        const location = geoResult.candidates[0].location;
        let result = getCircleGraphicsAndSquarePolygonFromCenter(location);
        squarePolygon = result.squarePolygon;
        circleGraphics = result.circleGraphics;
        centerPointGraphics = result.centerPointGraphic;
        // bbox = squarePolygon.rings;
      }

      // set polygon coordinates for search
      var polygon = new Polygon({ rings: bbox });

      const area = geometryEngine.geodesicArea(polygon, 'square-kilometers');

      //if area is less than 1km square then resize the bbox to 1km square
      if (area < 1) {
        let point = new Point({ x: polygon.centroid.x, y: polygon.centroid.y });
        const result = geometryEngine.geodesicBuffer(point, 501, 'meters') as Polygon;

        let xmin = result.extent.xmin;
        let ymin = result.extent.ymin;
        let xmax = result.extent.xmax;
        let ymax = result.extent.ymax;

        bbox = [
          [
            [xmin, ymax],
            [xmax, ymax],
            [xmax, ymin],
            [xmin, ymin],
            [xmin, ymax],
          ],
        ];

        // set polygon coordinates for search
        polygon = new Polygon({ rings: bbox, spatialReference: { wkid: 4326 } });
      }
      // if area is greater than 500km square then resize the bbox to 500km2
      else if (area > 500) {
        let point = new Point({
          x: polygon.centroid.x,
          y: polygon.centroid.y,
        });
        const result = geometryEngine.geodesicBuffer(point, 11181, 'meters') as Polygon;

        let xmin = result.extent.xmin;
        let ymin = result.extent.ymin;
        let xmax = result.extent.xmax;
        let ymax = result.extent.ymax;

        bbox = [
          [
            [xmin, ymax],
            [xmax, ymax],
            [xmax, ymin],
            [xmin, ymin],
            [xmin, ymax],
          ],
        ];

        // set polygon coordinates for search
        polygon = new Polygon({ rings: bbox, spatialReference: { wkid: 4326 } });
      }

      // FastView / Pleiades-Neo Guaranteed buffer-type sources: override the
      // geocoded bbox with a buffer_type-sized rectangle centered on the same
      // location, matching what the draw-on-map (customEventCreate) and
      // file-upload AOI paths already do via staticRectangleRef.
      let staticRectanglePolygon: any = null;
      let staticRectangleGraphic: any = null;
      let staticRectangleCenterGraphic: any = null;
      if (staticRectangleRef.current) {
        const { width: fvW, height: fvH } = staticRectangleRef.current!;
        const result = getRectangleFromCenter(
          { x: polygon.centroid.x, y: polygon.centroid.y },
          fvW,
          fvH
        );
        staticRectanglePolygon = result.rectanglePolygon;
        staticRectangleGraphic = result.rectangleGraphic;
        staticRectangleCenterGraphic = result.centerPointGraphic;
      }
      const isSar = sarSelectedRef.current === true;
      const isFastview = !!staticRectangleRef.current;

      // setting websokcet payload coordinates
      // default to the geocoded bbox, then let sar/fastview override it with
      // their own shape — mirrors the file-upload AOI handler above.
      let finalCoordinates = bbox;
      if (isSar) {
        finalCoordinates = squarePolygon?.rings;
      } else if (isFastview) {
        finalCoordinates = staticRectanglePolygon?.rings;
      }

      setPolygonCoordinates(finalCoordinates);

      // creating a rectangular shaped area
      const graphic = new Graphic({
        geometry: polygon,
        symbol: sketchVM.polygonSymbol.clone(),
        // hide the polygon if sar or fastview flow is active
        visible: !isSar && !isFastview,
      });
      spatialReferenceRef.current = 4326;
      extentRef.current = polygon?.extent;

      //removing graphics
      sketchVM.snappingOptions.featureSources.removeAll();
      previewLayer.source.elements.removeAll();
      sketchVM.layer.removeAll();
      intersectLayer.removeAll();

      //clearing the intersection area value
      await sketchVM.emit('update', { state: 'clearIntersectArea' });

      sketchVM.layer.add(graphic);
      //if the sar flow is active add the circle graphics
      isSar && sketchVM.layer.add(circleGraphics), sketchVM.layer.add(centerPointGraphics);
      //if the fastview flow is active add the static rectangle graphics
      if (isFastview) {
        sketchVM.layer.add(staticRectangleGraphic);
        sketchVM.layer.add(staticRectangleCenterGraphic);
      }
      //firing the update event to set area in MapBox
      if (isSar) {
        sketchVM.update([circleGraphics, centerPointGraphics], { tool: 'move' });
      } else if (isFastview) {
        sketchVM.update([staticRectangleGraphic, staticRectangleCenterGraphic], { tool: 'move' });
      } else {
        sketchVM.update(graphic);
      }

      // going to a given search value location
      view?.goTo({ target: polygon.extent }, { duration: 3000 });

      if (!checkArea(finalCoordinates)) return;
      //if the Order New tab is active the search call will be avoided
      if (buttonRef.current?.getAttribute('aria-selected') === 'true') {
        if (isArchiveLandingScreenRef.current === false) {
          getWebSocketPayload(bbox);
        }
      }

      // for tracking
      let polygonArea = geometryEngine.geodesicArea(polygon, 'square-kilometers');

      //for clearing the file polygon when drawing a polygon on search
      MixpanelTracking.getInstance().clearProperty('file');

      // get offering id, source and data type from cart items
      const eventData = getOfferingIdSourceAndDataType(cartItems);
      if (MixpanelTracking.getInstance().hasProperty('siderOrderNew') == true) {
        MixpanelTracking.getInstance().track(
          MixpanelEvent.ORDER_NEW_SEARCH_BOX_LOCATION_AOI_DEFINED,
          {
            location: selectedItem.text,
            coordinates: bbox[0],
            area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
            offering_ids: eventData?.uniqueOfferingIds,
            sources: eventData?.uniqueSourceList,
            data_type: eventData?.uniqueDataTypes,
          }
        );
      } else {
        MixpanelTracking.getInstance().track(
          MixpanelEvent.EXPLORE_EXISTING_SEARCH_BOX_LOCATION_SEARCH,
          {
            location: selectedItem.text,
            coordinates: bbox[0],
            area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
            offering_ids: eventData?.uniqueOfferingIds,
            sources: eventData?.uniqueSourceList,
            data_type: eventData?.uniqueDataTypes,
          }
        );
      }
    } catch (error) {
      customToaster(t('common:generalError'));
    }
  };

  const handleChangeText = async (text: string) => {
    text = text.trimStart();

    if (!text) return;

    // const geoResult = await getSuggestionsFromText(text);
    const geoResult: GeoResult = await fetch(
      `${process.env.NEXT_PUBLIC_ESRI_SUGGEST_URL}?` +
        new URLSearchParams({
          token: `${process.env.NEXT_PUBLIC_ESRI_MAP_CONFIG_KEY}`,
          text: text,
          langCode: 'en',
          f: 'json',
        }),
      { cache: 'no-store' }
    ).then(async value => {
      const result = await value.json();
      return result;
    });

    if (geoResult?.error) {
      customToaster(t('common:generalError'));
      return;
    }

    setResults(
      geoResult?.suggestions?.map(value => ({
        label: value?.text,
        magicKey: value?.magicKey,
      }))
    );
  };

  async function handleCoordinateText(text: string) {
    const values: (string | number)[] = text.split(',').map(value => value.trim());

    if (values.length !== 2) {
      customToaster(t('inputError'));
      return;
    }

    // decimal minute second regex
    // valid regex
    //-> 43° 39' 3.85"
    //-> 43°39'03.9" N
    //-> 43 39 3.85
    //-> 35 N
    //->  43° 39 3.85"
    //-> -45 N
    //-> 35

    const DMSregex = /^\s*(-?\d{1,3})(?:°?\s*(\d{1,2})'?\s*([\d.]+)"?)?\s*([NSEW])?\s*$/;
    const coordLiteralRegex = /^(-?\d{1,3}(?:\.\d+)?)(?:\s*([NSWE]))?$/;
    //this regex does not accept 35N,45W
    const DMSregex5a = /^\s*(-?\d{1,3})°?\s*(\d{1,2})'?\s*([\d.]+)"?\s*([NSEW])?\s*$/;

    const checkForRules = values.every((value, index) => {
      //check if the coordinate is in dms format
      const dmsValue = (value as string).match(DMSregex);
      const coordLiteralValue = (value as string).match(coordLiteralRegex);

      let number = value;

      //if it is in coordinate literal format like 43.65106 N or 43.65106N
      if (coordLiteralValue) {
        //the number format exist there
        number = Number(coordLiteralValue[1]);

        let direction = coordLiteralValue[2];
        // the direction value exist there

        //+ve N, +ve E, -ve W, -ve S
        if (number < 0 && direction) {
          customToaster(`${t('direction')} ${direction} ${t('noNegativeNumber')}: ${value}`);
          return false;
        }

        if (direction === 'W' || direction === 'S') number *= -1;
      }
      //if it in dms format convert it to decimal degrees
      else if (dmsValue) {
        //util function to convert the dms value to decimal
        const degrees = Number(dmsValue[1]);
        const minutes = Number(dmsValue[2] || '0');
        const seconds = Number(dmsValue[3] || '0');
        const direction = (dmsValue[4] || '').toUpperCase();

        //+ve N, +ve E, -ve W, -ve S
        if (degrees < 0 && direction) {
          customToaster(`${t('direction')} ${direction} ${t('noNegativeNumber')}: ${value}`);
          return false;
        }

        number = convertDMSCoordinateToDD(degrees, minutes, seconds, direction);
      }
      //if not in dms format it will be in string
      if (typeof number === 'string') number = Number(number);

      //if it is not valid number
      if (Number.isNaN(number)) {
        customToaster(`${t('invalidCoordinateFormat')}: ${value}`);
        return false;
      }

      //check if the first value lies between 90 and -90
      if (index === 0 && (number < -90 || number > 90)) {
        customToaster(`${t('latitudeOutOfRange')}: ${number}. ${t('latitudeRange')}`);
        return false;
      }

      //check if the second value lies between 180 and -180
      if (index === 1 && (number < -180 || number > 180)) {
        customToaster(`${t('longitudeOutOfRange')}: ${number}. ${t('longitudeRange')}`);
        return false;
      }

      //putting the converted valid number in array
      values[index] = number;

      return true;
    });

    //if all the rules passes
    if (checkForRules) {
      let coor = [values[0], values[1]] as number[];

      let point = new Point({ x: coor[1], y: coor[0] });
      const result = geometryEngine.geodesicBuffer(point, 2500, 'meters') as Polygon;

      let xmin = result.extent.xmin;
      let ymin = result.extent.ymin;
      let xmax = result.extent.xmax;
      let ymax = result.extent.ymax;

      let bbox = [
        [
          [xmin, ymax],
          [xmax, ymax],
          [xmax, ymin],
          [xmin, ymin],
          [xmin, ymax],
        ],
      ];

      let squarePolygon: any = null;
      let circleGraphics: any = null;
      let centerPointGraphic: any = null;
      //if sar flow is active
      if (sarSelectedRef.current === true) {
        // sarSelectedRef is also flipped on by SkySat Assured (which
        // reuses the SAR circle UI). Same suppression as the other
        // three SAR-toast call sites in this file.
        if (!orderNewIsGuaranteedCapture?.current) {
          customToaster(t('sarInfoToast'), 'success');
        }
        //object is location: {x: num,y:num}
        const location = { x: point.x, y: point.y };
        let result = getCircleGraphicsAndSquarePolygonFromCenter(location);
        squarePolygon = result.squarePolygon;
        circleGraphics = result.circleGraphics;
        // bbox = squarePolygon.rings;
      }

      // set polygon coordinates for search
      const polygon = new Polygon({ rings: bbox });

      // FastView / Pleiades-Neo Guaranteed buffer-type sources: override the
      // fixed 2.5km-buffer bbox with a buffer_type-sized rectangle centered
      // on the same coordinates, matching what the draw-on-map
      // (customEventCreate) and file-upload AOI paths already do via
      // staticRectangleRef.
      let staticRectanglePolygon: any = null;
      let staticRectangleGraphic: any = null;
      let staticRectangleCenterGraphic: any = null;
      if (staticRectangleRef.current) {
        const { width: fvW, height: fvH } = staticRectangleRef.current!;
        const result = getRectangleFromCenter({ x: point.x, y: point.y }, fvW, fvH);
        staticRectanglePolygon = result.rectanglePolygon;
        staticRectangleGraphic = result.rectangleGraphic;
        staticRectangleCenterGraphic = result.centerPointGraphic;
      }
      const isSar = sarSelectedRef.current === true;
      const isFastview = !!staticRectangleRef.current;

      // setting payload coordinates states for Order new
      // default to the 2.5km-buffer bbox, then let sar/fastview override it
      // with their own shape — mirrors the file-upload AOI handler above.
      let finalCoordinates = bbox;
      if (isSar) {
        finalCoordinates = squarePolygon?.rings;
      } else if (isFastview) {
        finalCoordinates = staticRectanglePolygon?.rings;
      }

      setPolygonCoordinates(finalCoordinates);

      // creating a rectangular shaped area
      const graphic = new Graphic({
        geometry: polygon,
        symbol: sketchVM.polygonSymbol.clone(),
        //if sar or fastview flow is active hide this polygon
        visible: !isSar && !isFastview,
      });
      extentRef.current = polygon?.extent;
      spatialReferenceRef.current = 4326;

      // removing graphics
      sketchVM.layer.removeAll();
      intersectLayer.removeAll();
      sketchVM.snappingOptions.featureSources.removeAll();
      //clearing the intersection area value
      await sketchVM.emit('update', { state: 'clearIntersectArea' });

      sketchVM.layer.add(graphic);

      //if the sar flow is active add the circle graphics
      isSar && sketchVM.layer.add(circleGraphics), sketchVM.layer.add(centerPointGraphic);
      //if the fastview flow is active add the static rectangle graphics
      if (isFastview) {
        sketchVM.layer.add(staticRectangleGraphic);
        sketchVM.layer.add(staticRectangleCenterGraphic);
      }
      //when sar is true then firing the update event to set area in Mapbox and also set limite update on circle else
      //firing the update event to set area in MapBox
      if (isSar) {
        sketchVM.update([circleGraphics, centerPointGraphic], { tool: 'move' });
      } else if (isFastview) {
        sketchVM.update([staticRectangleGraphic, staticRectangleCenterGraphic], { tool: 'move' });
      } else {
        sketchVM.update(graphic);
      }

      //the area is always 25km square so no need to check

      //if the Order New tab is active the search call will be avoided
      if (buttonRef.current?.getAttribute('aria-selected') === 'true') {
        // isArchiveNewDesignEnabledRef.current === false ||
        // isArchiveNewDesignEnabledRef.current === true &&
        if (isArchiveLandingScreenRef.current === false) {
          getWebSocketPayload(bbox);
        }
      }

      // going to a given coordinates location
      view?.goTo({ target: polygon.extent }, { duration: 3000 });

      // eslint-disable-next-line require-use-server
      const geoResult = await getLocationNameFromLongitudeAndLatitude(coor[1], coor[0]);

      //for tracking

      let polygonArea = geometryEngine.geodesicArea(polygon, 'square-kilometers');

      //for clearing the file polygon when drawing a polygon based on coordinates
      MixpanelTracking.getInstance().clearProperty('file');

      if (MixpanelTracking.getInstance().hasProperty('siderOrderNew') == true) {
        // get offering id, source and data type from cart items
        const eventData = getOfferingIdSourceAndDataType(cartItems);
        MixpanelTracking.getInstance().track(
          MixpanelEvent.ORDER_NEW_SEARCH_BOX_COORDINATE_AOI_DEFINED,
          {
            location: geoResult?.address?.LongLabel,
            coordinates: [coor[0], coor[1]],
            area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
            offering_ids: eventData?.uniqueOfferingIds,
            sources: eventData?.uniqueSourceList,
            data_type: eventData?.uniqueDataTypes,
          }
        );
      } else {
        MixpanelTracking.getInstance().track(MixpanelEvent.EE_SB_CS, {
          location: geoResult?.address?.LongLabel,
          coordinates: [coor[0], coor[1]],
          area: polygonArea < 0 ? polygonArea * -1 : polygonArea,
        });
      }
      return;
    }
  }
  //setting the autosuggest search debounce here
  const debounced = useDebouncedCallback(handleChangeText, 100);
  const debouncedCoordinates = useDebouncedCallback(handleCoordinateText, 500);

  const newPrice = calculateExistingItemPrice(cartItems) || 0;

  // Filtering the data

  const goToCart = () => {
    // get offering id, source and data type from cart items
    const eventData = getOfferingIdSourceAndDataType(cartItems);

    MixpanelTracking.getInstance().track(MixpanelEvent.EXPLORE_EXISTING_GO_TO_CART_CLICK, {
      total_price: newPrice,
      number_of_items: cartQuantity,
      offering_ids: eventData?.uniqueOfferingIds,
      sources: eventData?.uniqueSourceList,
      data_type: eventData?.uniqueDataTypes,
    });
  };

  const formControls = { control, setValue, getValues, errors };
  const searchBarProps = {
    searchValue: searchValue,
    results: results,
    webSocketRef: webSocketRef,
    setShowSkeleton: setShowSkeleton,
    handleSelection: handleSelection,
    debounced: debounced,
    debouncedCoordinates: debouncedCoordinates,
    t: t,
  };
  const searchButtonProps = {
    polygonCoordinates: polygonCoordinates,
    setIsArchiveLandingScreen: setIsArchiveLandingScreen,
    isArchiveLandingScreenRef: isArchiveLandingScreenRef,
    getWebSocketPayload: getWebSocketPayload,
  };

  const searchButtonHandler = () => {
    const startDate = getValues('startDate');
    const endDate = getValues('lastDate');

    MixpanelTracking.getInstance().track(MixpanelEvent.EXPLORE_EXISTING_DATA_TYPE_SEARCH, {
      location: searchValue,
      start_date: mixPanelDateFormat(startDate),
      end_date: mixPanelDateFormat(endDate),
      data_type: selectDataType,
      coordinates: polygonCoordinates[0],
    });

    if (!moment(startDate).isValid() || !moment(endDate).isValid()) {
      return customToasterUpdater(
        `${t('validDateRangeError', 'Please select a valid date range in future.')}`,
        'valid date'
      );
    }
    if (moment(endDate).isBefore(startDate)) {
      return customToasterUpdater(
        `${t('validDateRangeError', 'Please select a valid date range in future.')}`,
        'before-date'
      );
    }

    if (polygonCoordinates[0][0].length === 0) {
      // center of current view of map
      const center = view.center;
      //  extent of current view of map
      const extent = view.extent;

      // height and width of current view of map in kilometers
      const heightOfMap = extent.height / 1000;
      const widthofMap = extent.width / 1000;

      // draw the point at center of current view
      let point = new Point({ x: center.longitude, y: center.latitude });

      // area of desired square polygon
      let areaOfSquare = 0.2;

      if (heightOfMap > 0.2 && widthofMap > 0.2) {
        areaOfSquare = heightOfMap / 2;
      }

      // check if map is capable of drawing the 25km square poltgon
      if (heightOfMap >= 6 && widthofMap >= 6) {
        areaOfSquare = 2.5;
      }

      // Draw the square polygon
      const result = geometryEngine.geodesicBuffer(point, areaOfSquare, 'kilometers') as Polygon;

      let xmin = result.extent.xmin;
      let ymin = result.extent.ymin;
      let xmax = result.extent.xmax;
      let ymax = result.extent.ymax;

      let coordinates = [
        [
          [xmin, ymax],
          [xmax, ymax],
          [xmax, ymin],
          [xmin, ymin],
          [xmin, ymax],
        ],
      ];

      const squarePolygon = new Polygon({
        rings: coordinates,
        spatialReference: { wkid: 4326 },
      });
      const rectangleGraphic = new Graphic({
        geometry: squarePolygon,
        visible: true,
        symbol: sketchVM.polygonSymbol.clone(),
      });

      sketchVM.layer.removeAll();

      sketchVM.layer.add(rectangleGraphic);
      sketchVM.update(rectangleGraphic);

      //  go to the extent of the drawn polygon
      // set the coordinates to the variable
      view.goTo({ target: squarePolygon.extent }, { duration: 4000 });
      setPolygonCoordinates(squarePolygon.rings);

      //  change the screen and start the websocket to show data
      setIsArchiveLandingScreen(false);
      isArchiveLandingScreenRef.current = false;
      getWebSocketPayload(squarePolygon.rings);
    }
    if (polygonCoordinates[0][0].length > 0) {
      if (!checkArea(polygonCoordinates)) return;
      setIsArchiveLandingScreen(false);
      isArchiveLandingScreenRef.current = false;
      getWebSocketPayload(polygonCoordinates);
    }
  };
  return (
    <Box
      className={`sider-wrapper ${isHub ? 'cf-wrap' : ''}`} //class for hub
    >
      {/* For Explore Mobile Users Show Informational Dialog */}
      <MobileIntoDialog
        searchData={searchData}
        searchButtonProps={searchButtonProps}
        searchBarProps={searchBarProps}
      />

      <Box className="sider-content">
        <Box className="sider-search" sx={{ flexDirection: 'row-reverse', gap: '8px !important' }}>
          <SearchBar
            searchValue={searchValue}
            results={results}
            webSocketRef={webSocketRef}
            setShowSkeleton={setShowSkeleton}
            handleSelection={handleSelection}
            debounced={debounced}
            debouncedCoordinates={debouncedCoordinates}
            t={t}
          />
          <UploadDialog handleFileUpload={handleFileUpload} />
        </Box>

        <TabContext value={isNew?.toString()}>
          <TabList className="sider-tabs" onChange={handleChange}>
            <Tab
              ref={buttonRef}
              label={t('browseImagery', 'Browse Imagery')}
              value={K.TABS_VALUES.ARCHIVE}
            />
            <Tab
              label={t('captureNewImagery', 'Capture New Imagery')}
              value={K.TABS_VALUES.TASKING}
            />
          </TabList>
          <TabPanel value="1">
            <ExploreExisting
              searchBarComponentProps={{
                searchValue: searchValue,
                results: results,
                webSocketRef: webSocketRef,
                setShowSkeleton: setShowSkeleton,
                handleSelection: handleSelection,
                debounced: debounced,
                debouncedCoordinates: debouncedCoordinates,
              }}
              checkArea={checkArea}
              isArchiveLandingScreen={isArchiveLandingScreenRef.current}
              isArchiveLandingScreenRef={isArchiveLandingScreenRef}
              setIsArchiveLandingScreen={setIsArchiveLandingScreen}
              getWebSocketPayload={getWebSocketPayload}
              productCardContainerRef={productCardContainerRef}
              quickSketchVM={quickSketchVM}
              intersectLayer={intersectLayer}
              previewLayer={previewLayer}
              sketchVM={sketchVM}
              polygonCoordinates={polygonCoordinates}
              products={products}
              formControls={formControls}
              setFilterLastDate={setFilterLastDate}
              setFilterStartDate={setFilterStartDate}
              filterLastDate={filterLastDate}
              filterStartDate={filterStartDate}
              isFetchingData={isFetchingData}
              progress={progress}
              showSkeleton={showSkeleton}
              searchValue={searchValue}
              searchData={searchData}
              removeLayerAndHighlightedCard={removeLayerAndHighlightedCard}
              setPolygonCoordinates={setPolygonCoordinates}
              handleFileUpload={handleFileUpload}
              selectDataType={selectDataType}
              setSelectDataType={setSelectDataType}
              selectDataTypeRef={selectDataTypeRef}
              placeholder={placeholder}
            />
          </TabPanel>
          <TabPanel value="2">
            <Box className="order-new-wrapper order-new-container">
              <Typography sx={{ paddingBottom: '1rem' }} variant="body2">
                {t(
                  'onTitle1',
                  'Schedule a future imagery collection, subject to weather conditions and sensor availability.'
                )}
              </Typography>

              <TaskingForm
                coordinates={polygonCoordinates}
                sketchVM={sketchVM}
                locationName={searchValue}
                view={view}
                searchData={searchData}
                polygonArea={calculateArea(polygonCoordinates)}
              />
            </Box>
          </TabPanel>
        </TabContext>
      </Box>

      {isNew.toString() === '1' && (
        <>
          {isArchiveLandingScreenRef.current === true && (
            <Box className="cart-footer" sx={{ position: 'sticky', bottom: 0 }}>
              <Button variant="contained" size="large" fullWidth onClick={searchButtonHandler}>
                {t('search', 'Search')}
              </Button>
            </Box>
          )}

          {isArchiveLandingScreenRef.current === false && (
            <Box className="cart-footer" sx={{ position: 'sticky', bottom: 0 }}>
              <Stack direction="column" className="existing-cart-value">
                {multiProduct ? (
                  <Typography aria-label="mp-item-count" variant="body2">
                    <span>
                      {multiProductCartItemRef.current?.overlapData?.length
                        ? multiProductCartItemRef.current?.overlapData?.length
                        : '0'}
                    </span>
                    {` `}
                    {`${multiProductCartItemRef.current?.overlapData?.length > 1 ? 'items' : 'item'} selected`}
                  </Typography>
                ) : (
                  <Typography aria-label="item-count" variant="body2">
                    <span> {`${cartQuantity} `}</span>
                    {cartQuantity > 1 ? t('items') : t('item')}
                  </Typography>
                )}

                {multiProduct && (
                  <Typography aria-label="mp-max-cost" variant="body2">
                    <span>
                      {multiProductCartItemRef.current?.cardData?.price
                        ? `$${priceNumberFormat(multiProductCartItemRef.current?.cardData?.price)} `
                        : `$${priceNumberFormat(0)} `}
                    </span>
                    {t('orderMaxCost')}
                  </Typography>
                )}
                <Typography aria-label="cart-price" variant="body2">
                  <span> {`$${priceNumberFormat(newPrice)} `}</span>
                  {t('totalCartPrice')}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {multiProduct ? (
                  <AddMultiProductDialog
                    addToCartCallback={() => {
                      removeLayerAndHighlightedCard();
                      confirmMergeItemCart();
                      customToaster('Added to Cart', 'success');
                    }}
                  />
                ) : (
                  cartItems?.filter(data => data.type === orderType.EXISTING).length > 0 && (
                    <Link href="/cart">
                      <Button
                        aria-label="go-to-cart-btn"
                        size="large"
                        startIcon={<ShoppingCartOutlinedIcon />}
                        variant="contained"
                        onClick={goToCart}
                        name="Go to cart"
                      >
                        {t('gotoCart')}
                      </Button>
                    </Link>
                  )
                )}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
